import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import type { BuxusOperatorPayload } from '@buxus/shared'
import {
  assetIdAndName,
  assetHasOpenableDocuments,
  assetRowDisplayName,
  buildAssetHazardDetailModel,
  buildOperatorSlugMaps,
  findAssetIdByHazardId,
  findActiveAssetRow,
  findCatalogOperatorRow,
  findHazardRowInAsset,
  getVisibleAssets,
  isScreenVisible,
  findStakeholderSafeNavStack,
  menuTree,
  mergeAssetBranchesIntoMenu,
  mergeTestYourKnowledgeMenuLabels,
  operatorMapLegendItemsFromRecord,
  parseMapboxLngLat,
  normalizeStakeholderCode,
  orderStakeholderCodes,
  pageViewedScreenProps,
  AnalyticsScreenConstants,
  ALL_STAKEHOLDER_TYPES_ORDER,
  resolveOperatorSegmentToId,
  STAKEHOLDER_MENU_SWATCH_HEX,
  type MenuEntry,
  type OperatorSlugRow,
  resolveScreen,
  resolveSendAlertScreen,
  resolveHomeMapMode,
  REGISTRATION_PROFILE_JSON_KEY,
  canUseStakeholderSwitcher,
  type HomeMapMode,
  type ResolveContext,
} from '@buxus/shared'
import { DownloadOperatorsScreen } from './DownloadOperatorsScreen'
import { CollapsibleNavSidebar } from './components/nav/CollapsibleNavSidebar'
import { ProfileScreen } from './ProfileScreen'
import { InformationScreen } from './InformationScreen'
import { AllAssetsHubScreen } from './AllAssetsHubScreen'
import { HomeOperatorsMapScreen } from './HomeOperatorsMapScreen'
import { OperatorOverviewMapScreen } from './OperatorOverviewMapScreen'
import { Map as MapView } from './Map'
import { MapSurface } from './MapSurface'
import { OperatorLegendBar } from './components/OperatorLegendBar'
import { ProductsHazardsBrowse } from './ProductsHazardsBrowse'
import { FacilityProfileCard } from './FacilityProfileCard'
import { HazardDetailView } from './HazardDetailView'
import { AssetDocumentsList } from './AssetDocumentsList'
import { CompanyErpPicker } from './CompanyErpPicker'
import { CompanyResponseAccordions } from './CompanyResponseAccordions'
import { ResponderActionsAccordions } from './ResponderActionsAccordions'
import { RadioFrequencyAccordions } from './RadioFrequencyAccordions'
import { KeyLocationAccordions } from './KeyLocationAccordions'
import { EmergencyDialAction } from './components/EmergencyDialAction'
import { scrollMainContentToTop } from './platform/ui/mainScroll'
import { OperatorImageGallery } from './OperatorImageGallery'
import { ContactCardView } from './components/ContactCardView'
import { MakeARequestPanel } from './components/MakeARequestPanel'
import { RequestFormPanel } from './features/makeRequest/RequestFormPanel'
import { makeARequestHubPath, MAKE_A_REQUEST_SCREEN_ID } from './features/makeRequest/makeRequestPaths'
import { showAppErrorModal } from './platform/ui/appErrorModal'
import { AppBootLoading, AppLoadingRow } from './components/AppBootLoading'
import { takeBootLoadingLabel } from './platform/auth/bootLoadingLabel'
import { InlineSpinner } from './components/InlineSpinner'
import { isOperatorFileViewableInTab } from './platform/buxus/operatorFileOpen'
import { isOperatorFileAvailable } from './platform/buxus/operatorFileAvailability'
import { findHazardStorageMapView } from './hazardMapFromPayload'
import { SendAlertScreen } from './features/sendAlert/SendAlertScreen'
import { parseAppPath, pathForAppState, pathForMenuStackPrefix, analyticsRoutePathKey, shellScreenIdFromPathname } from './navigation/appPaths'
import {
  findActivePathIdsInTree,
  highlightContextFromRoute,
  highlightContextFromStack,
  isErHubAncestorForRoute,
  menuEntryScope,
  menuLeafIsActive,
  navTreeDescendantMatchesActive,
  type NavHighlightContext,
} from './navigation/navHighlight'
import { pathForOperatorRoute } from './navigation/operatorRoute'
import { HELP_OFFSITE_URL, PROFILE_STAKEHOLDER_STORAGE_KEY } from './platform/abp/config'
import { useAbp } from './platform/abp/react'
import { useOperatorSession } from './platform/buxus/OperatorSessionContext'
import type { OperatorWithVersionDto } from './platform/buxus/types'
import {
  resetAnalyticsSessionTracking,
  registerAnalyticsStakeholderType,
  EventTypes,
  trackEvent,
  trackPageViewIfChanged,
} from './platform/analytics/analytics'
import {
  trackAccordionToggle,
  trackNavigationRedirected,
  trackMenuItemSelected,
  trackRichHtmlLinkClick,
  trackUrlPressed,
  type ScreenInteractionContext,
} from './platform/analytics/interactionAnalytics'
import {
  fileExtFromFileName,
  normalizeStoredFileExtension,
  trackWebFileOpenFailed,
  trackWebFileOpenPressed,
  trackWebFileOpenStarted,
  trackWebFileOpenSucceeded,
  webFileOpenFailurePhaseFromError,
} from './platform/analytics/webFileOpenAnalytics'
import { mdiMenu, mdiOpenInNew } from '@mdi/js'
import { NavMdiIcon, TabIcons } from './NavTabIcons'
import { getOperatorLogoBlobRef, getOperatorLogoUrl } from './platform/buxus/operatorLogo'
import { OperatorPageHeaderLogo } from './components/OperatorPageHeaderLogo'
import {
  clearProfileJurisdictionCountyStateKeys,
  loadProfileJurisdictionCountyStateKeys,
} from './platform/auth/profileJurisdictionStorage'
import { syncProfileStakeholderFromRegistrationApi } from './platform/auth/oidc'
import { pinNeutralBrowserChromeTheme } from './platform/ui/browserChromeTheme'
import { resolveAppBarPrimaryHex } from './platform/ui/stakeholderAppBarTheme'
import { useMobileWebViewport } from './platform/ui/mobileViewport'
import { LMS_START_URI } from './training/trainingCopy'
import { TrainingDocumentDetailScreen } from './training/TrainingDocumentDetailScreen'
import { TrainingDocumentsListScreen } from './training/TrainingDocumentsListScreen'
import { TrainingPortalScreen } from './training/TrainingPortalScreen'
import {
  TRAINING_FEATURE_SCENARIO,
  TRAINING_FEATURE_TOOLBOX_TIP,
} from './training/trainingDocument'
import {
  isTrainingShellScreen,
  trainingBackTarget,
  TRAINING_LMS_SCREEN,
  TRAINING_SCENARIO_DETAIL_SCREEN,
  TRAINING_SCENARIOS_SCREEN,
  TRAINING_SCREEN,
  TRAINING_TOOLBOX_TIP_DETAIL_SCREEN,
  TRAINING_TOOLBOX_TIPS_SCREEN,
  trainingShellTitle,
} from './training/trainingScreens'
import './App.css'

const DEFAULT_SCREEN = 'screen.operatorOverview'
const HOME_SCREEN = '__home__'
const ALERT_SCREEN = '__alert__'
const PROFILE_SCREEN = '__profile__'
const INFORMATION_SCREEN = '__information__'
const HAZARD_SCREEN_ID = 'screen.assetHazard'

function stacksEqual(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((s, i) => s === b[i])
}

const LAST_SIDEBAR_ASSET_STORAGE_PREFIX = 'buxus.web.lastSidebarAsset.'

function readStoredSidebarAssetId(operatorId: string | null): string | null {
  if (!operatorId) return null
  try {
    const v = sessionStorage.getItem(`${LAST_SIDEBAR_ASSET_STORAGE_PREFIX}${operatorId}`)?.trim()
    return v || null
  } catch {
    return null
  }
}

function writeStoredSidebarAssetId(operatorId: string | null, assetId: string | null): void {
  if (!operatorId) return
  try {
    const key = `${LAST_SIDEBAR_ASSET_STORAGE_PREFIX}${operatorId}`
    if (assetId) sessionStorage.setItem(key, assetId)
    else sessionStorage.removeItem(key)
  } catch {
    /* ignore quota / private mode */
  }
}

function parseHazardProductSubtype(search: string): string | null {
  try {
    const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
    const v = q.get('hp')
    return v?.trim() ? v.trim() : null
  } catch {
    return null
  }
}

function isAssetContentScreen(screenId: string): boolean {
  return screenId.startsWith('screen.asset') && screenId !== 'screen.assets'
}

function sidebarHighlightAssetIdForScreen(
  screenId: string,
  activeAssetId: string | null,
  lastSidebarAssetId: string | null,
): string | null {
  if (screenId === 'screen.assets') return null
  if (!isAssetContentScreen(screenId)) return null
  return activeAssetId ?? lastSidebarAssetId
}

function parseHazardId(search: string): string | null {
  try {
    const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
    const v = q.get('hi')
    return v?.trim() ? v.trim() : null
  } catch {
    return null
  }
}

function parseContactArea(search: string): string | null {
  try {
    const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
    const v = q.get('ca')
    return v?.trim() ? v.trim() : null
  } catch {
    return null
  }
}

const BREADCRUMB_LABEL_MAX_LEN = 48

function formatContactAreaBreadcrumbLabel(area: string): string {
  const trimmed = area.trim()
  if (trimmed.length <= BREADCRUMB_LABEL_MAX_LEN) return trimmed
  return `${trimmed.slice(0, BREADCRUMB_LABEL_MAX_LEN - 1)}…`
}

/** Contacts child-nav stays on `screen.contacts`; area/contact filters use query params only. */
function childNavStackAppend(stack: string[], targetScreenId: string): string[] {
  const tail = stack[stack.length - 1]
  if (tail === targetScreenId && targetScreenId === 'screen.contacts') return stack
  return [...stack, targetScreenId]
}

function parseContactId(search: string): string | null {
  try {
    const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
    const v = q.get('ci')
    return v?.trim() ? v.trim() : null
  } catch {
    return null
  }
}

function parseRequestType(search: string): string | null {
  try {
    const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
    const v = q.get('rt')
    return v?.trim() ? v.trim() : null
  } catch {
    return null
  }
}

function isRequestFormView(screenId: string, requestType: string | null): boolean {
  if (!requestType) return false
  if (screenId === MAKE_A_REQUEST_SCREEN_ID) return true
  return screenId === INFORMATION_SCREEN && requestType === 'Request an Unlisted Operator'
}

function requestFormPageViewedProps(): { screen: string; screenConstant: 'REQUEST_FORM_SCREEN' } {
  return {
    screenConstant: 'REQUEST_FORM_SCREEN',
    screen: AnalyticsScreenConstants.REQUEST_FORM_SCREEN,
  }
}

const FILE_UNAVAILABLE_MESSAGE =
  'This document is not available. Re-download the operator on the Download screen and try again.'

function appendScreenQueryParams(
  basePath: string,
  item: {
    contactArea?: string
    contactId?: string
    requestType?: string
  },
): string {
  const params = new URLSearchParams()
  if (item.contactArea) params.set('ca', item.contactArea)
  if (item.contactId) params.set('ci', item.contactId)
  if (item.requestType) params.set('rt', item.requestType)
  const qs = params.toString()
  return qs ? `${basePath}?${qs}` : basePath
}

function findAssetBranchLabel(nodes: MenuEntry[], assetId: string): string | null {
  for (const n of nodes) {
    if (n.assetId === assetId && n.id.startsWith('nav.asset.')) return n.label
    if (n.children?.length) {
      const hit = findAssetBranchLabel(n.children, assetId)
      if (hit) return hit
    }
  }
  return null
}

function stakeholderTail(code: string): string {
  const parts = code.split(' - ')
  return parts.length > 1 ? parts[1]!.trim() : code.trim()
}

function isTopLevelShellScreen(id: string): boolean {
  return (
    id === '__download__' ||
    id === HOME_SCREEN ||
    id === '__operators__' ||
    isTrainingShellScreen(id) ||
    id === ALERT_SCREEN ||
    id === PROFILE_SCREEN ||
    id === INFORMATION_SCREEN
  )
}

/** Operator downloads that include per-stakeholder SAS JSON (not single-blob responders). */
function operatorExpectsStakeholderPayloads(
  operatorId: string,
  stakeholderUrlsByOperator: Record<string, Record<string, string>>,
  catalog: OperatorWithVersionDto[] | null,
): boolean {
  const urls = stakeholderUrlsByOperator[operatorId]
  if (urls && Object.keys(urls).length > 0) return true
  const row = catalog?.find((o) => o.operatorId === operatorId)
  if (row?.allStakeholderSas && typeof row.allStakeholderSas === 'object') {
    return Object.keys(row.allStakeholderSas as Record<string, string>).length > 0
  }
  return false
}

export default function App() {
  const navigate = useNavigate()
  const location = useLocation()
  const lastPathPushedRef = useRef<string>('')
  const abp = useAbp()
  const showSendAlertNav = abp.sessionContextLoaded && abp.canSendAlerts
  const operatorSession = useOperatorSession()
  const {
    persisted,
    catalog,
    catalogLoading,
    catalogUpdateBadgeCount,
    stakeholderCode,
    setStakeholderCode,
    getAvailableStakeholderCodes,
    getCachedOperatorPayload,
    ensureOperatorPayload,
    downloadedOperators,
    downloadingId,
    downloadAllBusy,
    downloadProgressByOperator,
    openOperatorFile,
    stakeholderUrlsByOperator,
    stakeholderCodesHydrated,
    getPersistedManifestFileIds,
  } = operatorSession

  const [openingOperatorFileId, setOpeningOperatorFileId] = useState<string | null>(null)

  /** Catalog list fetch or in-progress operator payload downloads — not `downloadingId` (background prefetch). */
  const downloadNavBusy =
    catalogLoading ||
    downloadAllBusy ||
    Object.keys(downloadProgressByOperator).length > 0

  const downloadNavSpinnerLabel =
    catalogLoading &&
    !downloadAllBusy &&
    Object.keys(downloadProgressByOperator).length === 0
      ? 'Updating operator list'
      : 'Downloading operators'

  const operatorSlugMaps = useMemo(() => {
    const rows: OperatorSlugRow[] = []
    const seen = new Set<string>()
    const add = (operatorId: string, tenancyName: string | undefined | null) => {
      const tn = tenancyName?.trim()
      if (!tn || seen.has(operatorId)) return
      seen.add(operatorId)
      rows.push({ operatorId, tenancyName: tn })
    }
    if (catalog?.length) {
      for (const op of catalog) {
        add(op.operatorId, op.tenancyName ?? persisted.entries[op.operatorId]?.tenancyName)
      }
    }
    for (const e of Object.values(persisted.entries)) {
      add(e.operatorId, e.tenancyName)
    }
    return buildOperatorSlugMaps(rows)
  }, [catalog, persisted.entries])

  const urlSlugForOperator = useCallback(
    (id: string) => {
      const fromMap = operatorSlugMaps.idToSlug.get(id)
      if (fromMap) return fromMap
      const cached = persisted.entries[id]?.tenancyName?.trim()
      return cached ? cached.toLowerCase() : ''
    },
    [operatorSlugMaps, persisted.entries],
  )

  const [selectedOperatorId, setSelectedOperatorId] = useState<string | null>(null)
  /** Drives sidebar section visibility when not on an operator screen (e.g. All operators). */
  const [lastNavigatedOperatorId, setLastNavigatedOperatorId] = useState<string | null>(
    () => persisted.activeOperatorId ?? null,
  )
  const [selectedPayload, setSelectedPayload] = useState<BuxusOperatorPayload | null>(null)
  /**
   * Stakeholder code used to load `selectedPayload` (same args as last successful `ensureOperatorPayload`).
   * While this differs from `effectiveStakeholderCode`, visibility checks must not run — otherwise we mix
   * a previous stakeholder's JSON with the new filter and spuriously reset the stack (mobile does not).
   */
  const [operatorPayloadStakeholder, setOperatorPayloadStakeholder] = useState<string | null>(null)
  /** Payload for menu pruning when it must differ from `selectedPayload` (index + no selection). */
  const [menuIsolationPayload, setMenuIsolationPayload] = useState<BuxusOperatorPayload | null>(null)
  const payload: BuxusOperatorPayload | null = selectedPayload
  const usingLivePayload = !!selectedPayload
  const [stack, setStack] = useState<string[]>(['__operators__'])
  const [navBootstrapped, setNavBootstrapped] = useState(false)
  const [payloadError, setPayloadError] = useState<string | null>(null)
  const [activeAssetId, setActiveAssetId] = useState<string | null>(null)
  /** Sidebar branch context; persists on assets hub while `activeAssetId` clears for hub content. */
  const [lastSidebarAssetId, setLastSidebarAssetId] = useState<string | null>(null)
  /**
   * Same-frame sidebar highlight before the router commits the next URL. Written synchronously in
   * sidebar / child-nav onClick; read during render + layout path sync, then cleared in useLayoutEffect.
   */
  const sidebarHighlightOverrideRef = useRef<NavHighlightContext | undefined>(undefined)
  const [navOpenById, setNavOpenById] = useState<Record<string, boolean>>({})
  const [utilityMenuOpen, setUtilityMenuOpen] = useState(false)
  const [stakeholderMenuOpen, setStakeholderMenuOpen] = useState(false)
  const [utilityMenuStakeholderMode, setUtilityMenuStakeholderMode] = useState(false)
  const isMobileWeb = useMobileWebViewport()
  const navInitializedRef = useRef(false)
  /**
   * Home pre-download catalog: after the first download completes, keep showing the download list
   * until the user leaves Home (e.g. Download tab). Avoids swapping to the map explorer mid-session.
   */
  const homeDownloadCatalogLatchRef = useRef(false)
  const profileStakeholderSyncedRef = useRef(false)
  const mainRef = useRef<HTMLElement>(null)
  const [profileStakeholderRaw, setProfileStakeholderRaw] = useState<string | null>(() => {
    try {
      return localStorage.getItem(PROFILE_STAKEHOLDER_STORAGE_KEY)
    } catch {
      return null
    }
  })
  const [profileJurisdictionKeys, setProfileJurisdictionKeys] = useState<ReadonlySet<string> | null>(
    () => loadProfileJurisdictionCountyStateKeys(),
  )

  const [bootLoadingLabel] = useState(() => takeBootLoadingLabel())

  const authReady = abp.ready
  const signedIn = abp.isAuthenticated

  /**
   * `probeSession` writes jurisdiction keys before `authReady`; React state can lag one frame.
   * Re-read from storage once authenticated so asset filters match the synced profile immediately.
   */
  const jurisdictionKeysForResolve = useMemo((): ReadonlySet<string> | undefined => {
    if (!signedIn || !authReady) return undefined
    return loadProfileJurisdictionCountyStateKeys() ?? undefined
  }, [signedIn, authReady, profileJurisdictionKeys])

  useLayoutEffect(() => {
    if (!signedIn || !authReady) return
    setProfileJurisdictionKeys(loadProfileJurisdictionCountyStateKeys())
    try {
      setProfileStakeholderRaw(localStorage.getItem(PROFILE_STAKEHOLDER_STORAGE_KEY))
    } catch {
      setProfileStakeholderRaw(null)
    }
  }, [signedIn, authReady])

  const screenId = stack[stack.length - 1] ?? '__operators__'

  useEffect(() => {
    if (screenId !== HOME_SCREEN) {
      homeDownloadCatalogLatchRef.current = false
      return
    }
    if (downloadedOperators.length === 0) {
      homeDownloadCatalogLatchRef.current = true
    }
  }, [screenId, downloadedOperators.length])

  /** Pre-download onboarding: Home shows the same operator catalog/download UI as Download. */
  const showOperatorDownloadSurface =
    screenId === '__download__' ||
    (screenId === HOME_SCREEN &&
      (downloadedOperators.length === 0 || homeDownloadCatalogLatchRef.current))

  const isHomeMapExplorer =
    screenId === HOME_SCREEN &&
    !showOperatorDownloadSurface &&
    downloadedOperators.length > 0

  const homeMapMode = useMemo((): HomeMapMode => {
    if (!signedIn) return 'operatorScoped'
    let profileJson: unknown = null
    try {
      const raw = localStorage.getItem(REGISTRATION_PROFILE_JSON_KEY)
      if (raw?.trim()) profileJson = JSON.parse(raw) as unknown
    } catch {
      /* ignore */
    }
    return resolveHomeMapMode({
      registrationProfileData: profileJson,
      profileStakeholderRaw,
    })
  }, [signedIn, profileStakeholderRaw, profileJurisdictionKeys])

  const homeScopedOperatorId = downloadedOperators[0]?.operatorId ?? null

  const isOperatorOverviewMap = screenId === 'screen.operatorOverview'
  const isAssetsMapHub = screenId === 'screen.assets'
  const isAssetOverviewMap = screenId === 'screen.assetOverview'
  const assetMapViewOverride = useMemo(() => {
    const jump = (location.state as { assetMapJump?: { lat: string; lng: string; zoom: string } } | null)
      ?.assetMapJump
    if (!jump?.lat?.trim() || !jump?.lng?.trim() || !jump?.zoom?.trim()) return null
    const center = parseMapboxLngLat(jump.lat, jump.lng, { rejectOrigin: true })
    const rawZoom = Number.parseFloat(jump.zoom)
    if (!center || !Number.isFinite(rawZoom)) return null
    return { center, zoom: Math.min(22, Math.max(0, rawZoom)) }
  }, [location.state, location.key])
  const isMapPrimaryFluidPage = isOperatorOverviewMap || isAssetOverviewMap
  const skipContentChildNav =
    isOperatorOverviewMap ||
    isAssetsMapHub ||
    isAssetOverviewMap

  useLayoutEffect(() => {
    scrollMainContentToTop(mainRef.current)
  }, [location.pathname, screenId, selectedOperatorId, activeAssetId, stack.length])

  const inAssetsSection =
    screenId === 'screen.assets' ||
    (screenId.startsWith('screen.asset') && screenId !== 'screen.assets')

  /** Sidebar tree context: persists on hub; includes in-flight picks before effect runs. */
  const sidebarAssetIdForMenu = lastSidebarAssetId ?? activeAssetId

  const assetMenuMergeOptions = useMemo(
    () => ({
      selectedAssetId: sidebarAssetIdForMenu,
      inAssetsSection,
    }),
    [sidebarAssetIdForMenu, inAssetsSection],
  )

  const hazardProductSubtype = useMemo(
    () => parseHazardProductSubtype(location.search),
    [location.search],
  )
  const hazardId = useMemo(() => parseHazardId(location.search), [location.search])
  const contactArea = useMemo(() => parseContactArea(location.search), [location.search])
  const contactId = useMemo(() => parseContactId(location.search), [location.search])
  const requestType = useMemo(() => parseRequestType(location.search), [location.search])

  /** Asset id from the URL path (Option B) — available same render as navigation, before React state catches up. */
  const parsedOperatorRoute = useMemo(() => {
    const parsed = parseAppPath(location.pathname)
    if (parsed?.kind !== 'operator') return null
    return parsed.route
  }, [location.pathname])

  const trainingDocId = useMemo(() => {
    const parsed = parseAppPath(location.pathname)
    return parsed?.kind === 'shell' ? parsed.trainingDocId : undefined
  }, [location.pathname])

  const isSitemapContentView =
    !!selectedOperatorId &&
    screenId !== '__operators__' &&
    screenId !== HOME_SCREEN &&
    screenId !== '__download__' &&
    !isTrainingShellScreen(screenId) &&
    screenId !== ALERT_SCREEN &&
    screenId !== PROFILE_SCREEN &&
    screenId !== INFORMATION_SCREEN
  const currentOperatorName = useMemo(() => {
    const id = selectedOperatorId
    if (id) {
      const fromPersisted = persisted.entries[id]?.name?.trim()
      if (fromPersisted) return fromPersisted
      const fromCatalog = catalog?.find((o) => o.operatorId === id)?.name?.trim()
      if (fromCatalog) return fromCatalog
    }
    const fromPayload = selectedPayload?.operator?.operatorName?.trim()
    if (fromPayload) return fromPayload
    return 'Operator'
  }, [selectedOperatorId, persisted.entries, catalog, selectedPayload])
  const stakeholderContextOperatorId =
    selectedOperatorId ?? lastNavigatedOperatorId ?? downloadedOperators[0]?.operatorId ?? null
  const availableStakeholderCodes = getAvailableStakeholderCodes(stakeholderContextOperatorId)
  const orderedStakeholderCodes = useMemo(
    () => orderStakeholderCodes(availableStakeholderCodes),
    [availableStakeholderCodes],
  )
  const stakeholderOptions = useMemo(
    () => orderedStakeholderCodes.map((code) => ({ code, label: stakeholderTail(code) })),
    [orderedStakeholderCodes],
  )

  const sendAlertScreenModel = useMemo(() => resolveSendAlertScreen(), [])

  const effectiveStakeholderCode = useMemo(() => {
    if (!stakeholderContextOperatorId) return null

    if (availableStakeholderCodes.length > 0) {
      if (stakeholderCode) {
        if (availableStakeholderCodes.includes(stakeholderCode)) return stakeholderCode
        const persistedCanon = normalizeStakeholderCode(stakeholderCode, ALL_STAKEHOLDER_TYPES_ORDER)
        if (persistedCanon) {
          const alias = availableStakeholderCodes.find(
            (raw) => normalizeStakeholderCode(raw, ALL_STAKEHOLDER_TYPES_ORDER) === persistedCanon,
          )
          if (alias) return alias
        }
      }
      return (
        normalizeStakeholderCode(profileStakeholderRaw, availableStakeholderCodes) ??
        orderedStakeholderCodes[0] ??
        availableStakeholderCodes[0] ??
        null
      )
    }

    /** SAS key list not hydrated yet (e.g. cold reload): still apply session / profile codes for asset filters. */
    if (stakeholderCode?.trim()) return stakeholderCode
    const pr = profileStakeholderRaw?.trim()
    if (pr) return pr
    return null
  }, [
    stakeholderContextOperatorId,
    availableStakeholderCodes,
    stakeholderCode,
    profileStakeholderRaw,
    orderedStakeholderCodes,
  ])

  const homeScopedStakeholderCode = useMemo(() => {
    if (!homeScopedOperatorId) return null
    const codesForOp = getAvailableStakeholderCodes(homeScopedOperatorId)
    if (
      effectiveStakeholderCode &&
      codesForOp.length > 0 &&
      codesForOp.includes(effectiveStakeholderCode)
    ) {
      return effectiveStakeholderCode
    }
    return null
  }, [homeScopedOperatorId, effectiveStakeholderCode, getAvailableStakeholderCodes])

  const appBarPrimaryHex = useMemo(
    () => resolveAppBarPrimaryHex(profileStakeholderRaw, effectiveStakeholderCode),
    [profileStakeholderRaw, effectiveStakeholderCode],
  )

  /** Browser tab/status chrome stays neutral; app-bar keeps stakeholder color (mobile parity). */
  useLayoutEffect(() => {
    if (!signedIn) return
    pinNeutralBrowserChromeTheme()
  }, [signedIn, location.pathname, effectiveStakeholderCode, selectedOperatorId])

  const catalogOperatorRow = useMemo(() => {
    if (!selectedOperatorId || !catalog?.length) return null
    return findCatalogOperatorRow(
      catalog as unknown as Record<string, unknown>[],
      selectedOperatorId,
    )
  }, [selectedOperatorId, catalog])

  const contentResolveContext = useMemo((): ResolveContext => {
    const persistedManifestFileIds = selectedOperatorId
      ? getPersistedManifestFileIds(selectedOperatorId)
      : undefined
    const resolvedAssetId =
      parsedOperatorRoute?.assetId ?? activeAssetId ?? lastSidebarAssetId ?? null
    const baseCtx: ResolveContext = {
      stakeholderCode: effectiveStakeholderCode,
      jurisdictionCountyStateKeys: jurisdictionKeysForResolve,
      catalogOperatorRow,
      persistedManifestFileIds,
      activeAssetId: resolvedAssetId,
    }
    return {
      ...baseCtx,
      hasLocalAssetDocuments: (assetId) => {
        if (!payload) return false
        return assetHasOpenableDocuments(payload, baseCtx, assetId)
      },
    }
  }, [
    effectiveStakeholderCode,
    jurisdictionKeysForResolve,
    catalogOperatorRow,
    payload,
    getPersistedManifestFileIds,
    selectedOperatorId,
    parsedOperatorRoute?.assetId,
    activeAssetId,
    lastSidebarAssetId,
  ])

  /** Avoid rendering asset map UI until payload matches stakeholder view (prevents base-blob flash). */
  const assetMapFiltersReady = useMemo(() => {
    if (!selectedOperatorId || !payload) return false
    if (operatorPayloadStakeholder === effectiveStakeholderCode) return true
    if (operatorPayloadStakeholder !== null) return false
    if (!operatorExpectsStakeholderPayloads(selectedOperatorId, stakeholderUrlsByOperator, catalog)) {
      return true
    }
    const codesForOp = getAvailableStakeholderCodes(selectedOperatorId)
    if (effectiveStakeholderCode && codesForOp.includes(effectiveStakeholderCode)) {
      return false
    }
    return true
  }, [
    selectedOperatorId,
    payload,
    operatorPayloadStakeholder,
    effectiveStakeholderCode,
    stakeholderUrlsByOperator,
    catalog,
    getAvailableStakeholderCodes,
  ])

  const jurisdictionKeysForMap = jurisdictionKeysForResolve ?? null

  /** Asset context for resolve/maps: state, sidebar memory, or `hi` lookup after hard reload. */
  const contentAssetId = useMemo(() => {
    const hi = hazardId?.trim() ?? ''
    const onAssetScreen =
      screenId.startsWith('screen.asset') && screenId !== 'screen.assets'
    const hazardRoute = screenId === HAZARD_SCREEN_ID

    const assetContainsHazard = (assetId: string | null): boolean => {
      if (!assetId || !payload || !hi) return false
      const row = findActiveAssetRow(payload, contentResolveContext, assetId)
      return !!row && !!findHazardRowInAsset(row, hi)
    }

    if (hi && payload && onAssetScreen) {
      const fromHi = findAssetIdByHazardId(
        payload,
        contentResolveContext,
        hi,
        hazardProductSubtype,
      )
      if (fromHi) return fromHi
    }

    if (activeAssetId) {
      if (!hi || !hazardRoute || assetContainsHazard(activeAssetId)) return activeAssetId
    }

    if (parsedOperatorRoute?.assetId) return parsedOperatorRoute.assetId

    if (lastSidebarAssetId) {
      if (!hi || !hazardRoute || assetContainsHazard(lastSidebarAssetId)) {
        return lastSidebarAssetId
      }
    }

    return activeAssetId ?? lastSidebarAssetId ?? null
  }, [
    activeAssetId,
    lastSidebarAssetId,
    parsedOperatorRoute?.assetId,
    payload,
    hazardId,
    hazardProductSubtype,
    screenId,
    contentResolveContext,
  ])

  const contentAssetName = useMemo(() => {
    if (!payload || !contentAssetId) return undefined
    const row = findActiveAssetRow(payload, contentResolveContext, contentAssetId)
    return row ? assetRowDisplayName(row) : undefined
  }, [payload, contentAssetId, contentResolveContext])

  const hazardAnalyticsContext = useMemo(() => {
    if (screenId !== HAZARD_SCREEN_ID || !payload || !hazardId?.trim()) return null
    const model = buildAssetHazardDetailModel(
      payload,
      contentResolveContext,
      contentAssetId,
      hazardId,
    )
    if (!model) return null
    const hazard = model.productSubType ?? model.pageTitle ?? undefined
    const storageLocationName = model.storageName ?? undefined
    if (!hazard && !storageLocationName) return null
    return { hazard, storageLocationName }
  }, [screenId, payload, contentResolveContext, contentAssetId, hazardId])

  const interactionContext = useMemo((): ScreenInteractionContext => {
    const { screen } = isRequestFormView(screenId, requestType)
      ? requestFormPageViewedProps()
      : pageViewedScreenProps(screenId)
    return {
      screen,
      operator: currentOperatorName !== 'Operator' ? currentOperatorName : undefined,
      asset: contentAssetName ?? undefined,
      ...(hazardAnalyticsContext?.hazard ? { hazard: hazardAnalyticsContext.hazard } : {}),
      ...(hazardAnalyticsContext?.storageLocationName
        ? { storageLocationName: hazardAnalyticsContext.storageLocationName }
        : {}),
    }
  }, [screenId, requestType, currentOperatorName, contentAssetName, hazardAnalyticsContext])

  const handleOpenOperatorFile = useCallback(
    async (
      fileId: string,
      options?: { fileExt?: string; fileName?: string; displayName?: string },
    ) => {
      const fileOpenBase = {
        screen: interactionContext.screen,
        fileId,
        fileName: options?.fileName,
        fileExt:
          normalizeStoredFileExtension(options?.fileExt) ??
          fileExtFromFileName(options?.fileName),
        title: options?.displayName,
      }
      const fileOpenExtra = {
        operator: interactionContext.operator,
        asset: interactionContext.asset,
        ...(interactionContext.hazard ? { hazard: interactionContext.hazard } : {}),
        ...(interactionContext.storageLocationName
          ? { storageLocationName: interactionContext.storageLocationName }
          : {}),
      }

      trackWebFileOpenPressed(fileOpenBase, fileOpenExtra)

      if (!selectedOperatorId) {
        trackWebFileOpenFailed(
          fileOpenBase,
          { errorPhase: 'unavailable', errorMessage: 'no_operator_selected' },
          fileOpenExtra,
        )
        showAppErrorModal({ message: 'Select an operator before opening files.' })
        return
      }
      setOpeningOperatorFileId(fileId)

      const operatorVersion = persisted.entries[selectedOperatorId]?.cachedVersion ?? null
      const available = await isOperatorFileAvailable(
        selectedOperatorId,
        operatorVersion,
        fileId,
        options?.fileExt,
        catalog as unknown as Record<string, unknown>[] | null,
      )
      if (!available) {
        trackWebFileOpenFailed(
          fileOpenBase,
          { errorPhase: 'unavailable', errorMessage: 'file_not_available' },
          fileOpenExtra,
        )
        showAppErrorModal({ message: FILE_UNAVAILABLE_MESSAGE })
        setOpeningOperatorFileId(null)
        return
      }

      const viewInTab = isOperatorFileViewableInTab(options?.fileExt)
      const viewTab = viewInTab ? window.open('about:blank', '_blank') : null
      if (viewInTab && !viewTab) {
        trackWebFileOpenFailed(
          fileOpenBase,
          {
            errorPhase: 'popup_blocked',
            errorMessage: 'popup_blocked',
          },
          fileOpenExtra,
        )
        showAppErrorModal({
          message: 'Could not open a new tab. Allow pop-ups for this site and try again.',
        })
        setOpeningOperatorFileId(null)
        return
      }

      trackWebFileOpenStarted(fileOpenBase, fileOpenExtra)

      try {
        await openOperatorFile(selectedOperatorId, { fileId, ...options, viewTab })
        trackWebFileOpenSucceeded(fileOpenBase, fileOpenExtra)
      } catch (e) {
        if (viewTab && !viewTab.closed) viewTab.close()
        const msg = e instanceof Error ? e.message : String(e)
        trackWebFileOpenFailed(
          fileOpenBase,
          {
            errorPhase: webFileOpenFailurePhaseFromError(msg),
            errorMessage: msg,
          },
          fileOpenExtra,
        )
        if (msg === 'FILE_NOT_IN_CATALOG') {
          showAppErrorModal({ message: FILE_UNAVAILABLE_MESSAGE })
        } else if (msg.includes('pop-up') || msg.includes('pop-ups')) {
          showAppErrorModal({ message: msg })
        } else {
          showAppErrorModal({ message: FILE_UNAVAILABLE_MESSAGE })
        }
      } finally {
        setOpeningOperatorFileId(null)
      }
    },
    [
      selectedOperatorId,
      openOperatorFile,
      catalog,
      persisted.entries,
      interactionContext,
    ],
  )

  const headerAssetId = useMemo(() => {
    if (isOperatorOverviewMap) return sidebarAssetIdForMenu
    if (isAssetContentScreen(screenId)) return contentAssetId
    return null
  }, [isOperatorOverviewMap, sidebarAssetIdForMenu, screenId, contentAssetId])

  const resolveAssetDisplayName = useCallback(
    (assetId: string | null): string | null => {
      if (!assetId || !payload) return null
      const assets = getVisibleAssets(payload, contentResolveContext)
      for (const row of assets) {
        const pair = assetIdAndName(row)
        if (pair?.id === assetId) return assetRowDisplayName(row as Record<string, unknown>)
      }
      return findAssetBranchLabel(
        mergeAssetBranchesIntoMenu(menuTree, payload, contentResolveContext, assetMenuMergeOptions),
        assetId,
      )
    },
    [payload, contentResolveContext, assetMenuMergeOptions],
  )

  const headerAssetDisplayName = useMemo(
    () => resolveAssetDisplayName(headerAssetId),
    [resolveAssetDisplayName, headerAssetId],
  )

  const showAssetInHeader = !!headerAssetId

  const hazardStorageMapView = useMemo(
    () =>
      findHazardStorageMapView({
        payload,
        assetId: contentAssetId,
        productSubtype: hazardProductSubtype,
        hazardId: screenId === HAZARD_SCREEN_ID ? hazardId : null,
      }),
    [payload, contentAssetId, hazardProductSubtype, hazardId, screenId],
  )

  /**
   * Registration default: resolve profile to a canonical CRM code, then pick this operator's
   * raw SAS key that maps to the same canonical (handles `T2`/`FA`/`SR` aliases, etc.).
   */
  const defaultStakeholderForOperator = useMemo(() => {
    if (!profileStakeholderRaw?.trim()) return null
    const profileCanon = normalizeStakeholderCode(
      profileStakeholderRaw.trim(),
      ALL_STAKEHOLDER_TYPES_ORDER,
    )
    if (!profileCanon) return null
    const match = orderedStakeholderCodes.find((raw) => {
      const rawCanon = normalizeStakeholderCode(raw, ALL_STAKEHOLDER_TYPES_ORDER)
      return rawCanon === profileCanon
    })
    return match ?? null
  }, [profileStakeholderRaw, orderedStakeholderCodes])

  const isViewingNonDefaultStakeholder =
    !!defaultStakeholderForOperator &&
    !!effectiveStakeholderCode &&
    effectiveStakeholderCode.trim().toLowerCase() !==
      defaultStakeholderForOperator.trim().toLowerCase()

  const canNavigateBack = useMemo(() => {
    if (stack.length > 1) return true
    if (selectedOperatorId && stack.length === 1) {
      const only = stack[0]
      if (only && !isTopLevelShellScreen(only)) return true
    }
    const top = stack[0]
    if (top && trainingBackTarget(top)) return true
    if (
      top === TRAINING_SCREEN ||
      top === ALERT_SCREEN ||
      top === PROFILE_SCREEN ||
      top === INFORMATION_SCREEN ||
      top === '__download__'
    ) {
      return true
    }
    return false
  }, [stack, selectedOperatorId])

  const selectedEntry = selectedOperatorId ? persisted.entries[selectedOperatorId] : undefined

  const operatorAnalyticsName = useMemo(() => {
    if (!selectedOperatorId) return undefined
    return (
      selectedEntry?.name ?? (usingLivePayload ? selectedPayload?.operator?.operatorName : undefined)
    )
  }, [
    selectedOperatorId,
    selectedEntry?.name,
    selectedEntry?.cachedVersion,
    usingLivePayload,
    selectedPayload?.operator?.operatorName,
  ])

  const goPath = useCallback(
    (opId: string | null, stackNext: string[], assetIdOverride?: string | null) => {
      const assetId = assetIdOverride !== undefined ? assetIdOverride : activeAssetId
      const p = pathForAppState(opId, stackNext, urlSlugForOperator, { assetId })
      lastPathPushedRef.current = p
      navigate(p, { replace: true })
    },
    [navigate, urlSlugForOperator, activeAssetId],
  )

  useEffect(() => {
    if (!signedIn) {
      setProfileStakeholderRaw(null)
      setProfileJurisdictionKeys(null)
      clearProfileJurisdictionCountyStateKeys()
      profileStakeholderSyncedRef.current = false
      navInitializedRef.current = false
      setNavBootstrapped(false)
      return
    }
    if (!authReady) return

    const applyStoredProfile = () => {
      try {
        setProfileStakeholderRaw(localStorage.getItem(PROFILE_STAKEHOLDER_STORAGE_KEY))
      } catch {
        setProfileStakeholderRaw(null)
      }
      setProfileJurisdictionKeys(loadProfileJurisdictionCountyStateKeys())
    }

    if (profileStakeholderSyncedRef.current) {
      applyStoredProfile()
      return
    }
    profileStakeholderSyncedRef.current = true
    void syncProfileStakeholderFromRegistrationApi().finally(applyStoredProfile)
  }, [signedIn, authReady])

  useEffect(() => {
    if (!authReady || !signedIn || navInitializedRef.current) return
    navInitializedRef.current = true
    setNavBootstrapped(true)
    const parsed = parseAppPath(location.pathname)
    if (parsed?.kind === 'operator') {
      const id = resolveOperatorSegmentToId(parsed.operatorSegment, operatorSlugMaps.slugToId)
      if (!id) {
        setSelectedOperatorId(null)
        setStack(['__operators__'])
        goPath(null, ['__operators__'])
        return
      }
      setLastNavigatedOperatorId(id)
      setSelectedOperatorId(id)
      setStack(parsed.stack)
      if (parsed.route.assetId) {
        setActiveAssetId(parsed.route.assetId)
        setLastSidebarAssetId(parsed.route.assetId)
        writeStoredSidebarAssetId(id, parsed.route.assetId)
      }
      lastPathPushedRef.current = pathForAppState(id, parsed.stack, urlSlugForOperator, {
        assetId: parsed.route.assetId,
      })
      return
    }
    if (parsed?.kind === 'shell') {
      setStack([parsed.screenId])
      lastPathPushedRef.current = pathForAppState(null, [parsed.screenId], urlSlugForOperator, {
        trainingDocId: parsed.trainingDocId,
      })
      if (parsed.screenId === '__operators__' || parsed.screenId === HOME_SCREEN) {
        setSelectedOperatorId(null)
      }
      return
    }
    if (downloadedOperators.length === 0) {
      setSelectedOperatorId(null)
      setStack([HOME_SCREEN])
      goPath(null, [HOME_SCREEN])
    } else {
      setSelectedOperatorId(null)
      setStack(['__operators__'])
      goPath(null, ['__operators__'])
    }
  }, [
    authReady,
    signedIn,
    downloadedOperators.length,
    goPath,
    location.pathname,
    operatorSlugMaps.slugToId,
    urlSlugForOperator,
  ])

  useEffect(() => {
    if (!authReady || !signedIn || !navBootstrapped) return
    const { pathname } = location
    if (pathname === lastPathPushedRef.current) return
    const parsed = parseAppPath(pathname)
    if (!parsed) {
      const fallback = pathForAppState(null, ['__operators__'], urlSlugForOperator)
      lastPathPushedRef.current = fallback
      navigate(fallback, { replace: true })
      return
    }
    lastPathPushedRef.current = pathname
    setUtilityMenuOpen(false)
    setStakeholderMenuOpen(false)
    if (parsed.kind === 'shell') {
      setStack([parsed.screenId])
      if (parsed.screenId === '__operators__' || parsed.screenId === HOME_SCREEN) {
        setSelectedOperatorId(null)
      }
      return
    }
    if (parsed.kind === 'operatorsIndex') {
      setSelectedOperatorId(null)
      setStack(['__operators__'])
      return
    }
    const resolvedId = resolveOperatorSegmentToId(parsed.operatorSegment, operatorSlugMaps.slugToId)
    if (!resolvedId) {
      setSelectedOperatorId(null)
      setStack(['__operators__'])
      const fallback = pathForAppState(null, ['__operators__'], urlSlugForOperator)
      lastPathPushedRef.current = fallback
      navigate(fallback, { replace: true })
      return
    }
    setLastNavigatedOperatorId(resolvedId)
    setSelectedOperatorId(resolvedId)
    setStack(parsed.stack)
    if (parsed.route.assetId) {
      setActiveAssetId(parsed.route.assetId)
      setLastSidebarAssetId(parsed.route.assetId)
      writeStoredSidebarAssetId(resolvedId, parsed.route.assetId)
    } else {
      const onAssetScreen =
        parsed.route.scope === 'assets' &&
        parsed.route.screenId !== 'screen.assets'
      if (!onAssetScreen) {
        setActiveAssetId(null)
        setLastSidebarAssetId(null)
        writeStoredSidebarAssetId(resolvedId, null)
      }
    }
  }, [
    location.pathname,
    authReady,
    signedIn,
    navBootstrapped,
    navigate,
    operatorSlugMaps.slugToId,
    urlSlugForOperator,
  ])

  useEffect(() => {
    if (!authReady || !signedIn || !navBootstrapped) return
    const parsed = parseAppPath(location.pathname)
    if (parsed?.kind !== 'operator') return
    if (!/^\d+$/.test(parsed.operatorSegment)) return
    if (!selectedOperatorId) return
    const expectedSlug = urlSlugForOperator(selectedOperatorId)
    if (expectedSlug === parsed.operatorSegment) return
    const canonical = pathForAppState(selectedOperatorId, parsed.stack, urlSlugForOperator, {
      assetId: parsed.route.assetId ?? activeAssetId,
    })
    if (canonical === location.pathname) return
    lastPathPushedRef.current = canonical
    navigate(canonical, { replace: true })
  }, [
    authReady,
    signedIn,
    navBootstrapped,
    location.pathname,
    selectedOperatorId,
    urlSlugForOperator,
    navigate,
  ])

  useEffect(() => {
    if (!signedIn || !authReady) return
    for (const op of downloadedOperators) {
      void ensureOperatorPayload(op.operatorId, null).catch(() => {
        /* offline / expired SAS — sidebar may show unpruned tree until retry */
      })
    }
  }, [signedIn, authReady, downloadedOperators, ensureOperatorPayload])

  useEffect(() => {
    if (!stakeholderContextOperatorId || !effectiveStakeholderCode) return
    if (stakeholderCode === effectiveStakeholderCode) return
    void setStakeholderCode(stakeholderContextOperatorId, effectiveStakeholderCode)
  }, [
    stakeholderContextOperatorId,
    stakeholderCode,
    effectiveStakeholderCode,
    setStakeholderCode,
  ])

  /** Until the user opens an operator, default menu context to the first download. */
  useEffect(() => {
    if (lastNavigatedOperatorId != null) return
    const firstId = downloadedOperators[0]?.operatorId
    if (firstId) setLastNavigatedOperatorId(firstId)
  }, [downloadedOperators, lastNavigatedOperatorId])

  useEffect(() => {
    registerAnalyticsStakeholderType(effectiveStakeholderCode ?? profileStakeholderRaw)
  }, [effectiveStakeholderCode, profileStakeholderRaw])

  useEffect(() => {
    if (!authReady || !signedIn || !navBootstrapped) return

    const shellFromPath = shellScreenIdFromPathname(location.pathname)
    // Wait until the nav stack matches the URL before tracking shell tabs (avoids bootstrap flicker).
    if (shellFromPath !== null && screenId !== shellFromPath) return

    const analyticsScreenId = shellFromPath ?? screenId
    const onRequestForm = isRequestFormView(analyticsScreenId, requestType)
    const screenProps = onRequestForm
      ? requestFormPageViewedProps()
      : pageViewedScreenProps(analyticsScreenId)
    const routePath = analyticsRoutePathKey(location.pathname)
    const operatorKey = isTopLevelShellScreen(analyticsScreenId)
      ? ''
      : (selectedOperatorId ?? '')
    const assetKey = contentAssetId ?? ''
    const hazardKey = screenId === HAZARD_SCREEN_ID ? (hazardId?.trim() ?? '') : ''
    const requestKey = onRequestForm ? (requestType ?? '') : ''
    const trainingDocKey = trainingDocId ?? ''
    const pageViewKey = `${routePath}|${screenProps.screenConstant}|${operatorKey}|${assetKey}|${hazardKey}|${requestKey}|${trainingDocKey}`
    trackPageViewIfChanged(pageViewKey, {
      ...screenProps,
      routePath: location.pathname,
      ...(operatorAnalyticsName ? { operator: operatorAnalyticsName } : {}),
      ...(contentAssetName ? { asset: contentAssetName } : {}),
      ...(hazardAnalyticsContext?.hazard ? { hazard: hazardAnalyticsContext.hazard } : {}),
      ...(hazardAnalyticsContext?.storageLocationName
        ? { storageLocationName: hazardAnalyticsContext.storageLocationName }
        : {}),
      ...(onRequestForm && requestType ? { request_type: requestType } : {}),
      ...(trainingDocId ? { documentId: trainingDocId } : {}),
    })
  }, [
    authReady,
    signedIn,
    navBootstrapped,
    location.pathname,
    screenId,
    selectedOperatorId,
    operatorAnalyticsName,
    contentAssetId,
    contentAssetName,
    hazardId,
    hazardAnalyticsContext,
    requestType,
    trainingDocId,
  ])

  useEffect(() => {
    if (!selectedOperatorId) {
      setSelectedPayload(null)
      setOperatorPayloadStakeholder(null)
      return
    }
    /**
     * Catalog refresh must not blank downloaded operators — payload comes from IndexedDB / memory.
     * Only wait on the catalog when this operator is not downloaded yet (needs live SAS from catalog).
     */
    if (catalogLoading && !persisted.entries[selectedOperatorId]) {
      setSelectedPayload(null)
      setOperatorPayloadStakeholder(null)
      return
    }
    if (
      operatorExpectsStakeholderPayloads(
        selectedOperatorId,
        stakeholderUrlsByOperator,
        catalog,
      ) &&
      !stakeholderCodesHydrated
    ) {
      setSelectedPayload(null)
      setOperatorPayloadStakeholder(null)
      return
    }
    const codesForOp = getAvailableStakeholderCodes(selectedOperatorId)
    if (codesForOp.length > 0 && !effectiveStakeholderCode) {
      setSelectedPayload(null)
      setOperatorPayloadStakeholder(null)
      return
    }
    setPayloadError(null)
    let cancelled = false
    /**
     * Catalog `allStakeholderSas` exists only for operator users; responders get a single main SAS
     * (their profile stakeholder JSON). Load the base blob from IDB and apply `effectiveStakeholderCode`
     * at resolve time — do not request a per-stakeholder SAS URL that is not on the catalog row.
     */
    const stakeForPayloadLoad =
      effectiveStakeholderCode && codesForOp.includes(effectiveStakeholderCode)
        ? effectiveStakeholderCode
        : null
    void (async () => {
      try {
        const p = await ensureOperatorPayload(selectedOperatorId, stakeForPayloadLoad)
        if (cancelled) return
        setSelectedPayload(p)
        setOperatorPayloadStakeholder(stakeForPayloadLoad != null ? effectiveStakeholderCode : null)
      } catch (e) {
        if (cancelled) return
        setPayloadError(e instanceof Error ? e.message : String(e))
        setSelectedPayload(null)
        setOperatorPayloadStakeholder(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [
    catalogLoading,
    effectiveStakeholderCode,
    ensureOperatorPayload,
    getAvailableStakeholderCodes,
    persisted.entries,
    selectedOperatorId,
    stakeholderCodesHydrated,
    stakeholderUrlsByOperator,
    catalog,
  ])

  /**
   * Sidebar sitemap context: prefer last/selected operator only if still downloaded. Otherwise the first
   * cached operator. Avoids showing the placeholder label "Operator" when `lastNavigatedOperatorId` points
   * at a stale id (e.g. before entries hydrate) or an entry that was cleared from disk.
   */
  const menuOperatorId = useMemo(() => {
    const hasEntry = (id: string | null | undefined): id is string =>
      typeof id === 'string' && id.length > 0 && !!persisted.entries[id]
    if (isSitemapContentView && hasEntry(selectedOperatorId)) return selectedOperatorId
    if (hasEntry(lastNavigatedOperatorId)) return lastNavigatedOperatorId
    if (hasEntry(selectedOperatorId)) return selectedOperatorId
    const firstId = downloadedOperators[0]?.operatorId
    return hasEntry(firstId) ? firstId : null
  }, [
    isSitemapContentView,
    lastNavigatedOperatorId,
    selectedOperatorId,
    downloadedOperators,
    persisted.entries,
  ])

  useEffect(() => {
    if (!lastNavigatedOperatorId) return
    if (persisted.entries[lastNavigatedOperatorId]) return
    setLastNavigatedOperatorId(downloadedOperators[0]?.operatorId ?? null)
  }, [lastNavigatedOperatorId, persisted.entries, downloadedOperators])

  useEffect(() => {
    if (!menuOperatorId) {
      setMenuIsolationPayload(null)
      return
    }
    /** Same operator as main content: reuse `selectedPayload` (avoid duplicate download). */
    if (menuOperatorId === selectedOperatorId) {
      setMenuIsolationPayload(null)
      return
    }
    if (catalogLoading && !persisted.entries[menuOperatorId]) {
      setMenuIsolationPayload(null)
      return
    }
    const codesForMenu = getAvailableStakeholderCodes(menuOperatorId)
    const stakeForMenuLoad =
      effectiveStakeholderCode && codesForMenu.includes(effectiveStakeholderCode)
        ? effectiveStakeholderCode
        : null
    let cancelled = false
    void (async () => {
      try {
        const p = await ensureOperatorPayload(menuOperatorId, stakeForMenuLoad)
        if (!cancelled) setMenuIsolationPayload(p)
      } catch {
        if (cancelled) return
        /** Catalog may still be hydrating; base blob from IDB is enough for sidebar pruning. */
        try {
          const base = await ensureOperatorPayload(menuOperatorId, null)
          if (!cancelled) setMenuIsolationPayload(base)
        } catch {
          if (!cancelled) setMenuIsolationPayload(null)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [
    catalogLoading,
    effectiveStakeholderCode,
    ensureOperatorPayload,
    getAvailableStakeholderCodes,
    menuOperatorId,
    persisted.entries,
    selectedOperatorId,
  ])

  const sidebarPrunePayload: BuxusOperatorPayload | null = useMemo(() => {
    if (!menuOperatorId) return null
    if (menuOperatorId === selectedOperatorId) {
      if (!assetMapFiltersReady) return selectedPayload
      return selectedPayload ?? getCachedOperatorPayload(menuOperatorId)
    }
    return menuIsolationPayload ?? getCachedOperatorPayload(menuOperatorId)
  }, [
    menuOperatorId,
    selectedOperatorId,
    selectedPayload,
    menuIsolationPayload,
    getCachedOperatorPayload,
    assetMapFiltersReady,
  ])

  useEffect(() => {
    const current = stack[stack.length - 1]
    if (
      !current ||
      current === '__download__' ||
      current === '__operators__' ||
      current === HOME_SCREEN ||
      isTrainingShellScreen(current) ||
      current === ALERT_SCREEN ||
      current === PROFILE_SCREEN ||
      current === INFORMATION_SCREEN
    )
      return
    if (!payload) return
    if (operatorPayloadStakeholder !== effectiveStakeholderCode) return

    const assetIdForResolve =
      parsedOperatorRoute?.assetId ?? activeAssetId ?? lastSidebarAssetId ?? null
    const nextStack = findStakeholderSafeNavStack(
      stack,
      payload,
      contentResolveContext,
      assetIdForResolve,
    )
    if (!nextStack || stacksEqual(nextStack, stack)) return

    const fromScreenProps = pageViewedScreenProps(stack[stack.length - 1] ?? '')
    const toScreenProps = pageViewedScreenProps(nextStack[nextStack.length - 1] ?? '')
    trackNavigationRedirected({
      screen: fromScreenProps.screen,
      operator: currentOperatorName !== 'Operator' ? currentOperatorName : undefined,
      asset: contentAssetName ?? undefined,
      reason: 'stakeholder_content_unavailable',
      fromScreen: fromScreenProps.screen,
      toScreen: toScreenProps.screen,
      fromScreenConstant: fromScreenProps.screenConstant,
      toScreenConstant: toScreenProps.screenConstant,
    })

    setStack(nextStack)
    if (selectedOperatorId) {
      const p = pathForAppState(selectedOperatorId, nextStack, urlSlugForOperator, {
        assetId: assetIdForResolve,
      })
      if (p === location.pathname) return
      lastPathPushedRef.current = p
      navigate(p, { replace: true })
    }
    const keepsAssetContext = nextStack.some(
      (s) => s.startsWith('screen.asset') && s !== 'screen.assets',
    )
    if (!keepsAssetContext) {
      setActiveAssetId(null)
      setLastSidebarAssetId(null)
      writeStoredSidebarAssetId(selectedOperatorId, null)
    } else if (assetIdForResolve) {
      setActiveAssetId(assetIdForResolve)
      setLastSidebarAssetId(assetIdForResolve)
      writeStoredSidebarAssetId(selectedOperatorId, assetIdForResolve)
    }
  }, [
    stack,
    payload,
    contentResolveContext,
    selectedOperatorId,
    navigate,
    urlSlugForOperator,
    operatorPayloadStakeholder,
    effectiveStakeholderCode,
    activeAssetId,
    lastSidebarAssetId,
    location.pathname,
    parsedOperatorRoute?.assetId,
    currentOperatorName,
    contentAssetName,
  ])

  useEffect(() => {
    const sid = stack[stack.length - 1] ?? ''
    const onAssetScreen = sid.startsWith('screen.asset') && sid !== 'screen.assets'
    const urlAssetId = parsedOperatorRoute?.assetId ?? null

    if (urlAssetId) {
      if (urlAssetId !== activeAssetId) {
        setActiveAssetId(urlAssetId)
        setLastSidebarAssetId(urlAssetId)
        writeStoredSidebarAssetId(selectedOperatorId, urlAssetId)
      }
      return
    }

    if (!onAssetScreen) {
      setActiveAssetId(null)
      return
    }
    if (activeAssetId) return

    const hi = parseHazardId(location.search)
    const onHazardRoute = sid === HAZARD_SCREEN_ID

    if (onHazardRoute && hi?.trim() && payload) {
      const found = findAssetIdByHazardId(
        payload,
        contentResolveContext,
        hi,
        parseHazardProductSubtype(location.search),
      )
      if (found) {
        setActiveAssetId(found)
        setLastSidebarAssetId(found)
        writeStoredSidebarAssetId(selectedOperatorId, found)
        return
      }
    }

    const fromStorage = readStoredSidebarAssetId(selectedOperatorId)
    if (fromStorage) {
      setActiveAssetId(fromStorage)
    }
  }, [
    stack,
    activeAssetId,
    selectedOperatorId,
    location.search,
    payload,
    contentResolveContext,
    parsedOperatorRoute?.assetId,
  ])

  useEffect(() => {
    if (activeAssetId) {
      setLastSidebarAssetId(activeAssetId)
      writeStoredSidebarAssetId(selectedOperatorId, activeAssetId)
    }
  }, [activeAssetId, selectedOperatorId])

  useEffect(() => {
    setLastSidebarAssetId(readStoredSidebarAssetId(selectedOperatorId))
  }, [selectedOperatorId])

  const commitSidebarAssetSelection = useCallback(
    (assetId: string) => {
      setActiveAssetId(assetId)
      setLastSidebarAssetId(assetId)
      writeStoredSidebarAssetId(selectedOperatorId, assetId)
    },
    [selectedOperatorId],
  )

  const openAssetsNavSection = useCallback(() => {
    setNavOpenById((prev) => (prev['nav.assets'] === true ? prev : { ...prev, 'nav.assets': true }))
  }, [])

  const clearContentAssetSelection = useCallback(() => {
    setActiveAssetId(null)
  }, [])

  const resolveOpts = useMemo(() => {
    const base: {
      activeAssetId?: string | null
      hazardId?: string | null
      contactArea?: string | null
      contactId?: string | null
      requestType?: string | null
    } = {}

    if (screenId === 'screen.contacts') {
      if (contactArea) base.contactArea = contactArea
      if (contactId) base.contactId = contactId
    }
    if (screenId === MAKE_A_REQUEST_SCREEN_ID && requestType) {
      base.requestType = requestType
    }

    if (
      !contentAssetId ||
      !screenId.startsWith('screen.asset') ||
      screenId === 'screen.assets'
    ) {
      return Object.keys(base).length ? base : undefined
    }
    if (screenId === HAZARD_SCREEN_ID) {
      return {
        ...base,
        activeAssetId: contentAssetId,
        hazardId,
      }
    }
    return { ...base, activeAssetId: contentAssetId }
  }, [screenId, contentAssetId, hazardId, contactArea, contactId, requestType])

  const resolved = useMemo(
    () =>
      !payload ||
      screenId === '__download__' ||
      screenId === '__operators__' ||
      screenId === HOME_SCREEN ||
      isTrainingShellScreen(screenId) ||
      screenId === ALERT_SCREEN ||
      screenId === PROFILE_SCREEN ||
      screenId === INFORMATION_SCREEN
        ? null
        : resolveScreen(screenId, payload, contentResolveContext, resolveOpts),
    [payload, screenId, contentResolveContext, resolveOpts],
  )

  const operatorName = currentOperatorName

  const headerCatalogRow = useMemo(
    () =>
      selectedOperatorId && catalog
        ? (catalog.find((o) => o.operatorId === selectedOperatorId) ?? null)
        : null,
    [catalog, selectedOperatorId],
  )
  const logoSrc = useMemo(() => {
    const fromCatalog = getOperatorLogoUrl(headerCatalogRow as unknown)
    const fromPayload = getOperatorLogoUrl(payload?.operator)
    const persistedUrl = selectedEntry?.logoImageSasUrl?.trim()
    const fromPersisted = persistedUrl
      ? getOperatorLogoUrl({ logoImageSasUrl: persistedUrl })
      : null
    const catalogRowById =
      selectedOperatorId && catalog && !headerCatalogRow
        ? (catalog.find((o) => o.operatorId === selectedOperatorId) ?? null)
        : null
    const fromCatalogWhenRowLoadedLater = getOperatorLogoUrl(catalogRowById as unknown)
    return (
      fromCatalog ??
      fromCatalogWhenRowLoadedLater ??
      fromPayload ??
      fromPersisted
    )
  }, [headerCatalogRow, catalog, selectedOperatorId, payload, selectedEntry])

  const logoBlobRef = useMemo(
    () => getOperatorLogoBlobRef(payload?.operator),
    [payload?.operator],
  )

  const headerTitle =
    trainingShellTitle(screenId) ??
    (screenId === '__download__'
      ? 'Download'
      : screenId === HOME_SCREEN
        ? 'Home'
        : screenId === '__operators__'
          ? 'Operators'
          : screenId === ALERT_SCREEN
            ? 'Send Alert'
            : screenId === PROFILE_SCREEN
              ? 'Profile'
              : screenId === INFORMATION_SCREEN
                ? 'Help'
                : (resolved?.title ?? operatorName))

  const goBack = useCallback(() => {
    if (!canNavigateBack) return
    scrollMainContentToTop(mainRef.current)
    if (stack.length > 1) {
      const next = stack.slice(0, -1)
      setStack(next)
      goPath(selectedOperatorId, next)
      return
    }
    const top = stack[0]
    const trainingParent = top ? trainingBackTarget(top) : null
    if (trainingParent) {
      setStack([trainingParent])
      goPath(null, [trainingParent])
      return
    }
    if (
      top === TRAINING_SCREEN ||
      top === ALERT_SCREEN ||
      top === PROFILE_SCREEN ||
      top === INFORMATION_SCREEN ||
      top === '__download__'
    ) {
      setStack(['__operators__'])
      goPath(null, ['__operators__'])
      return
    }
    if (selectedOperatorId && stack.length === 1 && stack[0] && !isTopLevelShellScreen(stack[0])) {
      setSelectedOperatorId(null)
      setStack(['__operators__'])
      goPath(null, ['__operators__'])
    }
  }, [canNavigateBack, goPath, selectedOperatorId, stack])

  /** Index keeps last viewed operator context for content navigation. */
  const navigateToOperatorsIndex = useCallback(() => {
    setActiveAssetId(null)
    setSelectedOperatorId(null)
    setStack(['__operators__'])
    goPath(null, ['__operators__'])
  }, [goPath])

  const goToOperatorScreen = useCallback(
    (operatorId: string, targetScreenId: string) => {
      setUtilityMenuOpen(false)
      setStakeholderMenuOpen(false)
      setLastNavigatedOperatorId(operatorId)
      setSelectedOperatorId(operatorId)
      setActiveAssetId(null)
      setStack([targetScreenId])
      const p = pathForAppState(operatorId, [targetScreenId], urlSlugForOperator)
      lastPathPushedRef.current = p
      navigate(p, { replace: true })
    },
    [navigate, urlSlugForOperator],
  )

  const mapPipelineOperatorDirectory = useMemo(
    () =>
      downloadedOperators.map((op) => ({
        operatorId: op.operatorId,
        name: op.name,
      })),
    [downloadedOperators],
  )

  const onViewPipelineOperator = useCallback(
    (operatorId: string) => {
      goToOperatorScreen(operatorId, DEFAULT_SCREEN)
    },
    [goToOperatorScreen],
  )

  const operatorLegendItems = useCallback(
    (operatorId: string) => {
      const payload = getCachedOperatorPayload(operatorId)
      return operatorMapLegendItemsFromRecord(payload?.operator)
    },
    [getCachedOperatorPayload],
  )

  const onSelectAssetFromMap = useCallback(
    (assetId: string) => {
      if (!selectedOperatorId) return
      const nextStack = [DEFAULT_SCREEN, 'screen.assets', 'screen.assetOverview']
      sidebarHighlightOverrideRef.current = {
        scope: 'assets',
        screenId: 'screen.assetOverview',
        assetId,
      }
      commitSidebarAssetSelection(assetId)
      openAssetsNavSection()
      setStack(nextStack)
      const p = pathForAppState(selectedOperatorId, nextStack, urlSlugForOperator, { assetId })
      lastPathPushedRef.current = p
      navigate(p)
    },
    [
      selectedOperatorId,
      commitSidebarAssetSelection,
      openAssetsNavSection,
      urlSlugForOperator,
      navigate,
    ],
  )

  const onFocusAssetFromMap = useCallback(
    (assetId: string) => {
      if (!selectedOperatorId) return
      commitSidebarAssetSelection(assetId)
      openAssetsNavSection()
    },
    [selectedOperatorId, commitSidebarAssetSelection, openAssetsNavSection],
  )

  const allAssetsHeaderLabel = useMemo(() => {
    if (!isAssetsMapHub || !payload) return null
    const count = getVisibleAssets(payload, contentResolveContext).length
    return count > 0 ? `All Assets (${count})` : 'All Assets'
  }, [isAssetsMapHub, payload, contentResolveContext])

  const breadcrumbItems = useMemo(() => {
    if (
      screenId === '__download__' ||
      screenId === '__operators__' ||
      screenId === HOME_SCREEN ||
      isTrainingShellScreen(screenId) ||
      screenId === ALERT_SCREEN ||
      screenId === PROFILE_SCREEN ||
      screenId === INFORMATION_SCREEN
    )
      return []
    if (!selectedOperatorId) return []
    const items: { label: string; to: string; active?: boolean }[] = [
      {
        label: 'Operators',
        to: pathForAppState(null, ['__operators__'], urlSlugForOperator),
      },
      {
        label: currentOperatorName,
        to: pathForAppState(selectedOperatorId, [DEFAULT_SCREEN], urlSlugForOperator),
      },
    ]
    const assetCrumbLabel =
      activeAssetId && payload
        ? findAssetBranchLabel(
            mergeTestYourKnowledgeMenuLabels(
              mergeAssetBranchesIntoMenu(
              menuTree,
              payload,
              contentResolveContext,
              assetMenuMergeOptions,
            ),
              payload,
              contentResolveContext,
            ),
            activeAssetId,
          )
        : null

    stack.forEach((id, idx) => {
      if (idx > 0 && stack[idx - 1] === id) return
      const isLast = idx === stack.length - 1
      const isFormView = isLast && id === MAKE_A_REQUEST_SCREEN_ID && !!requestType
      const title = payload
        ? resolveScreen(id, payload, contentResolveContext, {
            activeAssetId:
              isLast &&
              id.startsWith('screen.asset') &&
              id !== 'screen.assets' &&
              activeAssetId
                ? activeAssetId
                : undefined,
          })?.title
        : undefined
      if (!title) return
      const label =
        id === 'screen.assets'
          ? (() => {
              const count = payload ? getVisibleAssets(payload, contentResolveContext).length : 0
              return count > 0 ? `All Assets (${count})` : 'All Assets'
            })()
          : title
      items.push({
        label,
        to: pathForMenuStackPrefix(
          selectedOperatorId,
          stack.slice(0, idx + 1),
          activeAssetId,
          urlSlugForOperator,
        ),
        active: isLast && !isFormView,
      })
      if (
        idx === 0 &&
        id === 'screen.assets' &&
        assetCrumbLabel &&
        stack.some((s) => s.startsWith('screen.asset') && s !== 'screen.assets')
      ) {
        items.push({
          label: assetCrumbLabel,
          to: pathForMenuStackPrefix(
            selectedOperatorId,
            ['screen.assets', 'screen.assetOverview'],
            activeAssetId,
            urlSlugForOperator,
          ),
          active: false,
        })
      }
    })
    if (screenId === 'screen.contacts' && contactArea?.trim()) {
      const areaLabel = formatContactAreaBreadcrumbLabel(contactArea)
      const last = items[items.length - 1]
      if (last?.label !== areaLabel) {
        if (last?.active && last.label === 'Contacts') {
          last.active = false
        }
        items.push({
          label: areaLabel,
          to: appendScreenQueryParams(
            pathForAppState(selectedOperatorId, stack, urlSlugForOperator, {
              assetId: activeAssetId,
            }),
            { contactArea },
          ),
          active: !contactId,
        })
      }
    }
    if (requestType && screenId === MAKE_A_REQUEST_SCREEN_ID) {
      items.push({
        label: requestType,
        to: appendScreenQueryParams(makeARequestHubPath(selectedOperatorId, urlSlugForOperator), {
          requestType,
        }),
        active: true,
      })
    }
    return items
  }, [
    activeAssetId,
    assetMenuMergeOptions,
    contactArea,
    contactId,
    currentOperatorName,
    payload,
    requestType,
    screenId,
    selectedOperatorId,
    stack,
    contentResolveContext,
    urlSlugForOperator,
  ])

  const visibleMenuTree = useMemo(() => {
    if (!sidebarPrunePayload) return []
    const merged = mergeTestYourKnowledgeMenuLabels(
      mergeAssetBranchesIntoMenu(
        menuTree,
        sidebarPrunePayload,
        contentResolveContext,
        assetMenuMergeOptions,
      ),
      sidebarPrunePayload,
      contentResolveContext,
    )
    const filterNodes = (nodes: MenuEntry[]): MenuEntry[] => {
      const out: MenuEntry[] = []
      for (const node of nodes) {
        const visibleChildren = node.children ? filterNodes(node.children) : []
        const screenVisible = node.screenId
          ? isScreenVisible(node.screenId, sidebarPrunePayload, contentResolveContext)
          : false
        if (!screenVisible && visibleChildren.length === 0) continue
        const { children: _unusedChildren, ...nodeRest } = node
        out.push({
          ...nodeRest,
          ...(visibleChildren.length > 0 ? { children: visibleChildren } : {}),
          ...(screenVisible ? {} : { screenId: undefined }),
        })
      }
      return out
    }
    return filterNodes(merged)
  }, [sidebarPrunePayload, contentResolveContext, assetMenuMergeOptions])

  /** Operator sidebar block: only when payload is ready and the pruned tree has items. */
  const showOperatorSidebar = useMemo(() => {
    if (!isSitemapContentView || !menuOperatorId) return false
    if (!sidebarPrunePayload) return false
    return visibleMenuTree.length > 0
  }, [isSitemapContentView, menuOperatorId, sidebarPrunePayload, visibleMenuTree.length])

  /**
   * Sidebar highlight follows parsed URL scope + screen (not stack segment equality). Override ref
   * covers the one frame before location/state catch up after a sidebar click.
   */
  const sidebarHighlightFromUrl = useMemo((): NavHighlightContext => {
    const parsed = parseAppPath(location.pathname)
    if (parsed?.kind !== 'operator') {
      return highlightContextFromStack(stack, activeAssetId ?? lastSidebarAssetId)
    }
    const resolvedId = resolveOperatorSegmentToId(parsed.operatorSegment, operatorSlugMaps.slugToId)
    if (!resolvedId || !menuOperatorId || resolvedId !== menuOperatorId) {
      return highlightContextFromStack(stack, activeAssetId ?? lastSidebarAssetId)
    }
    const assetId =
      parsed.route.assetId ??
      sidebarHighlightAssetIdForScreen(
        parsed.route.screenId,
        activeAssetId,
        lastSidebarAssetId,
      )
    return highlightContextFromRoute({
      scope: parsed.route.scope,
      screenId: parsed.route.screenId,
      assetId,
    })
  }, [
    location.pathname,
    menuOperatorId,
    operatorSlugMaps.slugToId,
    stack,
    activeAssetId,
    lastSidebarAssetId,
  ])

  const sidebarHighlightContext: NavHighlightContext =
    sidebarHighlightOverrideRef.current !== undefined
      ? sidebarHighlightOverrideRef.current
      : sidebarHighlightFromUrl

  const getOperatorDisplayName = (operatorId: string): string =>
    persisted.entries[operatorId]?.name?.trim() ||
    catalog?.find((o) => o.operatorId === operatorId)?.name?.trim() ||
    'Operator'

  useLayoutEffect(() => {
    if (visibleMenuTree.length === 0) return
    const parsed = parseAppPath(location.pathname)
    if (parsed?.kind !== 'operator') return
    const sidebarContextAssetId = lastSidebarAssetId ?? activeAssetId
    const highlightCtx =
      sidebarHighlightOverrideRef.current !== undefined
        ? sidebarHighlightOverrideRef.current
        : highlightContextFromRoute({
            scope: parsed.route.scope,
            screenId: parsed.route.screenId,
            assetId:
              parsed.route.assetId ??
              sidebarHighlightAssetIdForScreen(
                parsed.route.screenId,
                activeAssetId,
                lastSidebarAssetId,
              ),
          })
    sidebarHighlightOverrideRef.current = undefined

    const pathIds = findActivePathIdsInTree(visibleMenuTree, highlightCtx)
    setNavOpenById((prev) => {
      let changed = false
      const next = { ...prev }
      /** Open ancestors for the active route only — never collapse unrelated branches the user left open. */
      if (pathIds?.length) {
        for (const id of pathIds) {
          if (next[id] !== true) {
            next[id] = true
            changed = true
          }
        }
      }
      const screenForPath = highlightCtx.screenId
      const inAssetScreens =
        screenForPath === 'screen.assets' ||
        (screenForPath.startsWith('screen.asset') && screenForPath !== 'screen.assets')
      if (inAssetScreens) {
        if (next['nav.assets'] !== true) {
          next['nav.assets'] = true
          changed = true
        }
        if (sidebarContextAssetId) {
          const assetBranchId = `nav.asset.${sidebarContextAssetId}`
          if (next[assetBranchId] !== true) {
            next[assetBranchId] = true
            changed = true
          }
          if (highlightCtx.scope === 'in-an-emergency') {
            const erFolderId = `${assetBranchId}:sk.emergencyResponse`
            if (next[erFolderId] !== true) {
              next[erFolderId] = true
              changed = true
            }
          }
        }
      }
      return changed ? next : prev
    })
  }, [
    location.pathname,
    location.search,
    visibleMenuTree,
    activeAssetId,
    lastSidebarAssetId,
  ])

  const renderNavNode = (node: MenuEntry, depth = 0) => {
    const children = node.children ?? []
    const hasChildren = children.length > 0
    const isNodeOpen = navOpenById[node.id] ?? node.defaultOpen ?? false
    const nodeRoute = node.screenId
      ? {
          scope: menuEntryScope(node),
          screenId: node.screenId,
          assetId: node.assetId ?? null,
        }
      : null
    const inOperatorShell =
      sidebarHighlightContext.screenId !== '__operators__' &&
      sidebarHighlightContext.screenId !== HOME_SCREEN &&
      sidebarHighlightContext.screenId !== '__download__' &&
      !isTrainingShellScreen(sidebarHighlightContext.screenId) &&
      sidebarHighlightContext.screenId !== ALERT_SCREEN &&
      sidebarHighlightContext.screenId !== PROFILE_SCREEN &&
      sidebarHighlightContext.screenId !== INFORMATION_SCREEN

    const navClickProps = {
      onClick: () => {
        trackMenuItemSelected({
          ...interactionContext,
          component: 'SidebarNav',
          menu: 'operator_tree',
          selection: node.label,
          navigationSource: 'sidebar',
        })
        if (nodeRoute) {
          sidebarHighlightOverrideRef.current = nodeRoute
        }
        if (node.id === 'nav.assets') openAssetsNavSection()
        if (node.assetId) commitSidebarAssetSelection(node.assetId)
        else clearContentAssetSelection()
      },
    }

    /**
     * Branch label interaction is expand-only (never collapse on label click).
     * Most branch rows use a Link: the label navigates to that row's hub/content screen and must
     * keep the subtree open so children stay visible — collapsing on the same click would contradict
     * navigation. Collapse is caret-only (toggle below).
     * Folder-only rows (no screenId) use expand-only on a button; rows with screenId + children
     * (e.g. Emergency Response) navigate to the hub via the label link.
     */
    const expandBranchOnLabelClick = () => {
      setNavOpenById((prev) => {
        if (prev[node.id] === true) return prev
        trackAccordionToggle(true, node.label, interactionContext)
        return { ...prev, [node.id]: true }
      })
    }

    const toggleBranchOnCaretClick = () => {
      const nextOpen = !isNodeOpen
      trackAccordionToggle(nextOpen, node.label, interactionContext)
      setNavOpenById((prev) => ({ ...prev, [node.id]: nextOpen }))
    }

    if (!hasChildren) {
      const leafActive =
        inOperatorShell && menuLeafIsActive(node, sidebarHighlightContext)
      const leafClass = leafActive ? 'nav-btn nav-tree-btn active' : 'nav-btn nav-tree-btn'
      const pad = { paddingLeft: `${0.65 + Math.min(depth, 6) * 0.6}rem` }
      if (!menuOperatorId || !nodeRoute) {
        return (
          <span key={node.id} className={`${leafClass} nav-tree-btn-disabled`} style={pad}>
            {node.label}
          </span>
        )
      }
      const leafTo = pathForOperatorRoute(menuOperatorId, nodeRoute, urlSlugForOperator)
      return (
        <Link
          key={node.id}
          to={leafTo}
          className={leafClass}
          style={pad}
          {...navClickProps}
        >
          {node.label}
        </Link>
      )
    }

    return (
      <div key={node.id} className="nav-tree-group">
        <div className="nav-tree-group-row">
          {node.screenId ? (
            menuOperatorId && nodeRoute ? (
              <Link
                to={pathForOperatorRoute(menuOperatorId, nodeRoute, urlSlugForOperator)}
                className={(() => {
                  if (!inOperatorShell) return 'nav-btn nav-tree-btn'
                  const self = menuLeafIsActive(node, sidebarHighlightContext)
                  const desc = navTreeDescendantMatchesActive(node, sidebarHighlightContext)
                  const erAncestor = isErHubAncestorForRoute(node, sidebarHighlightContext)
                  if (self && !desc) return 'nav-btn nav-tree-btn active'
                  if ((self && desc) || erAncestor) return 'nav-btn nav-tree-btn nav-tree-ancestor'
                  return 'nav-btn nav-tree-btn'
                })()}
                style={{ paddingLeft: `${0.65 + Math.min(depth, 6) * 0.6}rem` }}
                onClick={() => {
                  navClickProps.onClick()
                  expandBranchOnLabelClick()
                }}
              >
                {node.label}
              </Link>
            ) : (
              <span
                className="nav-btn nav-tree-btn nav-tree-btn-disabled"
                style={{ paddingLeft: `${0.65 + Math.min(depth, 6) * 0.6}rem` }}
              >
                {node.label}
              </span>
            )
          ) : (
            /* No screenId — expand-only label; see expandBranchOnLabelClick (e.g. Emergency Response). */
            <button
              type="button"
              className="nav-btn nav-tree-btn nav-tree-expand-label"
              style={{ paddingLeft: `${0.65 + Math.min(depth, 6) * 0.6}rem` }}
              aria-expanded={isNodeOpen}
              aria-label={`Expand ${node.label}`}
              onClick={expandBranchOnLabelClick}
            >
              {node.label}
            </button>
          )}
          {/* Caret: sole control that collapses an open branch (label is expand-only). */}
          <button
            type="button"
            className="nav-tree-caret-btn"
            aria-label={isNodeOpen ? `Collapse ${node.label}` : `Expand ${node.label}`}
            aria-expanded={isNodeOpen}
            onClick={toggleBranchOnCaretClick}
          >
            {isNodeOpen ? '▾' : '▸'}
          </button>
        </div>
        <div className="nav-tree-children">
          {isNodeOpen
            ? children.map((c) => renderNavNode(c, depth + 1))
            : null}
        </div>
      </div>
    )
  }

  const logout = () => {
    const screenProps = pageViewedScreenProps(screenId)
    trackEvent(EventTypes.USER_LOGGED_OUT, {
      screen: interactionContext.screen,
      screenConstant: screenProps.screenConstant,
      operator: interactionContext.operator,
      asset: interactionContext.asset,
    })
    resetAnalyticsSessionTracking()
    void abp.logout().then(() => {
      navInitializedRef.current = false
      setNavBootstrapped(false)
      lastPathPushedRef.current = ''
      setSelectedOperatorId(null)
      setLastNavigatedOperatorId(null)
      setMenuIsolationPayload(null)
      setSelectedPayload(null)
      setStack(['__operators__'])
      navigate('/', { replace: true })
    })
  }

  const mobileStakeholderLabel = effectiveStakeholderCode
    ? stakeholderTail(effectiveStakeholderCode)
    : orderedStakeholderCodes[0]
      ? stakeholderTail(orderedStakeholderCodes[0])
      : ''

  const appBarStakeholderDisplay = mobileStakeholderLabel

  const stakeholderSwitcherBusy =
    !!downloadingId ||
    !!(stakeholderContextOperatorId && downloadProgressByOperator[stakeholderContextOperatorId])

  const registrationProfileData = useMemo((): unknown | null => {
    if (!signedIn) return null
    try {
      const raw = localStorage.getItem(REGISTRATION_PROFILE_JSON_KEY)
      if (raw?.trim()) return JSON.parse(raw) as unknown
    } catch {
      /* ignore */
    }
    return null
  }, [signedIn, profileStakeholderRaw])

  const canSwitchStakeholder =
    !!stakeholderContextOperatorId &&
    canUseStakeholderSwitcher({
      registrationProfileData,
      availableStakeholderCodeCount: orderedStakeholderCodes.length,
    })
  const showStakeholderInAppBar = canSwitchStakeholder && !isMobileWeb

  useEffect(() => {
    if (!utilityMenuOpen) setUtilityMenuStakeholderMode(false)
  }, [utilityMenuOpen])

  useEffect(() => {
    if (isMobileWeb) setStakeholderMenuOpen(false)
  }, [isMobileWeb])

  const closeUtilityMenu = useCallback(() => {
    setUtilityMenuOpen(false)
    setUtilityMenuStakeholderMode(false)
  }, [])

  const closeStakeholderMenu = useCallback(() => {
    setStakeholderMenuOpen(false)
    setUtilityMenuStakeholderMode(false)
  }, [])

  const selectStakeholderCode = useCallback(
    (code: string) => {
      if (!stakeholderContextOperatorId || !canSwitchStakeholder) return
      trackMenuItemSelected({
        component: isMobileWeb ? 'UtilityMenu' : 'StakeholderSwitcherMenu',
        menu: 'stakeholder_switcher',
        selection: code,
      })
      closeStakeholderMenu()
      closeUtilityMenu()
      void setStakeholderCode(stakeholderContextOperatorId, code)
    },
    [
      canSwitchStakeholder,
      closeStakeholderMenu,
      closeUtilityMenu,
      isMobileWeb,
      setStakeholderCode,
      stakeholderContextOperatorId,
    ],
  )

  const openHelpExternal = () => {
    trackMenuItemSelected({
      component: 'UtilityMenu',
      menu: 'utility',
      selection: "What's New",
    })
    closeUtilityMenu()
    setStakeholderMenuOpen(false)
    window.open(HELP_OFFSITE_URL, '_blank', 'noopener,noreferrer')
  }

  if (!authReady) {
    return <AppBootLoading label={bootLoadingLabel} />
  }

  const shellStyle = {
    ['--stakeholder-primary' as string]: appBarPrimaryHex,
    ['--stakeholder-primary-contrast' as string]: '#ffffff',
  } as CSSProperties

  return (
    <div className="shell" style={shellStyle}>
      <header className="app-bar" style={{ backgroundColor: appBarPrimaryHex }} role="banner">
        <div className="app-bar-row">
          <div className="app-bar-side app-bar-side-left">
            <button
              type="button"
              className={`app-bar-back${canNavigateBack ? '' : ' app-bar-back-disabled'}`}
              onClick={goBack}
              aria-label="Back"
              disabled={!canNavigateBack}
            >
              <svg className="app-bar-back-icon" viewBox="0 0 24 24" aria-hidden>
                <path
                  fill="currentColor"
                  d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"
                />
              </svg>
            </button>
          </div>
          <div className="app-bar-center">
            <h1 className="app-bar-title">
              <span>{headerTitle}</span>
            </h1>
          </div>
          <div className="app-bar-side app-bar-side-right">
            {showStakeholderInAppBar ? (
              <div className="app-bar-stakeholder-cluster">
                <span className="app-bar-viewing-as">Viewing as:</span>
                <button
                  type="button"
                  className="app-bar-stakeholder-trigger"
                  onClick={() => {
                    setUtilityMenuOpen(false)
                    setUtilityMenuStakeholderMode(false)
                    setStakeholderMenuOpen((v) => !v)
                  }}
                  disabled={stakeholderSwitcherBusy}
                >
                  <span className="app-bar-stakeholder-trigger-label">{appBarStakeholderDisplay}</span>
                </button>
              </div>
            ) : null}
            {/* Hidden for now per UX request.
            <button
              type="button"
              className="app-bar-text-btn"
              onClick={() => void abp.setCulture(abp.cultureName === 'en' ? 'es' : 'en')}
              title="Toggle language"
            >
              {abp.cultureName.toUpperCase()}
            </button>
            */}
            <button
              type="button"
              className="app-bar-menu-btn"
              onClick={() => {
                setStakeholderMenuOpen(false)
                setUtilityMenuOpen((v) => {
                  if (v) setUtilityMenuStakeholderMode(false)
                  return !v
                })
              }}
              title="Menu"
            >
              <NavMdiIcon path={mdiMenu} className="app-bar-menu-icon" />
            </button>
          </div>
        </div>
        {stakeholderMenuOpen && canSwitchStakeholder && !isMobileWeb ? (
          <div className="stakeholder-menu">
            {stakeholderOptions.map((opt) => {
              const active = effectiveStakeholderCode === opt.code
              const swatch = STAKEHOLDER_MENU_SWATCH_HEX[opt.label] ?? '#94a3b8'
              const isDefault =
                defaultStakeholderForOperator !== null && opt.code === defaultStakeholderForOperator
              return (
                <button
                  key={opt.code}
                  type="button"
                  className={active ? 'stakeholder-menu-item active' : 'stakeholder-menu-item'}
                  onClick={() => selectStakeholderCode(opt.code)}
                >
                  <span className="stakeholder-swatch" style={{ background: swatch }} />
                  <span>
                    {opt.label}
                    {isDefault ? ' (Default)' : ''}
                  </span>
                  {active ? <span className="stakeholder-check">✓</span> : null}
                </button>
              )
            })}
            {isViewingNonDefaultStakeholder && defaultStakeholderForOperator ? (
              <button
                type="button"
                className="stakeholder-menu-item stakeholder-menu-muted"
                onClick={() => {
                  trackMenuItemSelected({
                    component: 'StakeholderSwitcherMenu',
                    menu: 'stakeholder_switcher',
                    selection: 'back_to_default',
                  })
                  closeStakeholderMenu()
                  closeUtilityMenu()
                  if (stakeholderContextOperatorId) {
                    void setStakeholderCode(stakeholderContextOperatorId, defaultStakeholderForOperator)
                  }
                }}
              >
                Back to default
              </button>
            ) : null}
          </div>
        ) : null}
        {utilityMenuOpen ? (
          <div
            className={[
              'utility-menu',
              isMobileWeb && utilityMenuStakeholderMode ? 'utility-menu--stakeholder' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {isMobileWeb && utilityMenuStakeholderMode ? (
              <>
                {stakeholderOptions.map((opt) => {
                  const active = effectiveStakeholderCode === opt.code
                  const swatch = STAKEHOLDER_MENU_SWATCH_HEX[opt.label] ?? '#94a3b8'
                  const isDefault =
                    defaultStakeholderForOperator !== null && opt.code === defaultStakeholderForOperator
                  return (
                    <button
                      key={opt.code}
                      type="button"
                      className={active ? 'stakeholder-menu-item active' : 'stakeholder-menu-item'}
                      onClick={() => selectStakeholderCode(opt.code)}
                      disabled={stakeholderSwitcherBusy}
                    >
                      <span className="stakeholder-swatch" style={{ background: swatch }} />
                      <span>
                        {opt.label}
                        {isDefault ? ' (Default)' : ''}
                      </span>
                      {active ? <span className="stakeholder-check">✓</span> : null}
                    </button>
                  )
                })}
                {isViewingNonDefaultStakeholder && defaultStakeholderForOperator ? (
                  <button
                    type="button"
                    className="stakeholder-menu-item stakeholder-menu-muted"
                    onClick={() => {
                      trackMenuItemSelected({
                        component: 'UtilityMenu',
                        menu: 'stakeholder_switcher',
                        selection: 'back_to_default',
                      })
                      closeUtilityMenu()
                      if (stakeholderContextOperatorId) {
                        void setStakeholderCode(stakeholderContextOperatorId, defaultStakeholderForOperator)
                      }
                    }}
                    disabled={stakeholderSwitcherBusy}
                  >
                    Back to default
                  </button>
                ) : null}
              </>
            ) : (
              <>
                <Link
                  to="/profile"
                  className="utility-menu-item"
                  onClick={() => {
                    trackMenuItemSelected({
                      component: 'UtilityMenu',
                      menu: 'utility',
                      selection: 'Profile',
                    })
                    closeUtilityMenu()
                  }}
                >
                  Profile
                </Link>
                <Link
                  to="/information"
                  className="utility-menu-item"
                  onClick={() => {
                    trackMenuItemSelected({
                      component: 'UtilityMenu',
                      menu: 'utility',
                      selection: 'Help',
                    })
                    closeUtilityMenu()
                  }}
                >
                  Help
                </Link>
                <button type="button" className="utility-menu-item" onClick={openHelpExternal}>
                  What's New?
                </button>
                <button type="button" className="utility-menu-item" onClick={logout}>
                  {abp.l('Logout') === 'Logout' ? 'Log out' : abp.l('Logout')}
                </button>
                {isMobileWeb && canSwitchStakeholder ? (
                  <>
                    <div className="utility-menu-separator" role="separator" />
                    <button
                      type="button"
                      className="utility-menu-item"
                      onClick={() => {
                        trackMenuItemSelected({
                          component: 'UtilityMenu',
                          menu: 'utility',
                          selection: 'stakeholder_switcher',
                        })
                        setUtilityMenuStakeholderMode(true)
                      }}
                      disabled={stakeholderSwitcherBusy}
                    >
                      Switch Stakeholder
                    </button>
                  </>
                ) : null}
              </>
            )}
          </div>
        ) : null}
      </header>
      <div className="body">
        <CollapsibleNavSidebar
          analytics={interactionContext}
          primaryNav={
            <div className="nav-primary-tabs">
              <Link
                to="/home"
                className={screenId === HOME_SCREEN ? 'nav-primary-tab active' : 'nav-primary-tab'}
                title="Home"
                onClick={() =>
                  trackMenuItemSelected({
                    component: 'PrimaryNav',
                    menu: 'bottom_tabs',
                    selection: 'Home',
                  })
                }
              >
                <NavMdiIcon path={TabIcons.home} />
                <span>Home</span>
              </Link>
              <Link
                to="/download"
                className={screenId === '__download__' ? 'nav-primary-tab active' : 'nav-primary-tab'}
                title="Download"
                onClick={() =>
                  trackMenuItemSelected({
                    component: 'PrimaryNav',
                    menu: 'bottom_tabs',
                    selection: 'Download',
                  })
                }
              >
                <NavMdiIcon path={TabIcons.download} />
                <span className="nav-primary-tab-label">
                  <span>Download</span>
                  {downloadNavBusy || catalogUpdateBadgeCount > 0 ? (
                    <span className="nav-download-indicators">
                      {catalogUpdateBadgeCount > 0 ? (
                        <span
                          className="nav-download-badge"
                          aria-label={`${catalogUpdateBadgeCount} updates available`}
                        >
                          {catalogUpdateBadgeCount > 99 ? '99+' : catalogUpdateBadgeCount}
                        </span>
                      ) : null}
                      {downloadNavBusy ? (
                        <span
                          className="nav-download-spinner"
                          role="status"
                          aria-label={downloadNavSpinnerLabel}
                        />
                      ) : null}
                    </span>
                  ) : null}
                </span>
              </Link>
              <Link
                to="/operators"
                className={
                  screenId === '__operators__' ? 'nav-primary-tab active' : 'nav-primary-tab'
                }
                title="Operators"
                onClick={() =>
                  trackMenuItemSelected({
                    component: 'PrimaryNav',
                    menu: 'bottom_tabs',
                    selection: 'Operators',
                  })
                }
              >
                <NavMdiIcon path={TabIcons.operators} />
                <span>Operators</span>
              </Link>
              <Link
                to="/training"
                className={
                  isTrainingShellScreen(screenId) ? 'nav-primary-tab active' : 'nav-primary-tab'
                }
                title="Training"
                onClick={() =>
                  trackMenuItemSelected({
                    component: 'PrimaryNav',
                    menu: 'bottom_tabs',
                    selection: 'Training',
                  })
                }
              >
                <NavMdiIcon path={TabIcons.training} />
                <span>Training</span>
              </Link>
              {showSendAlertNav ? (
              <Link
                to="/send-alert"
                className={screenId === ALERT_SCREEN ? 'nav-primary-tab active' : 'nav-primary-tab'}
                title="Send Alert"
                onClick={() =>
                  trackMenuItemSelected({
                    component: 'PrimaryNav',
                    menu: 'bottom_tabs',
                    selection: 'Send Alert',
                  })
                }
              >
                <NavMdiIcon path={TabIcons.alert} />
                <span>Send Alert</span>
              </Link>
              ) : null}
            </div>
          }
          operatorNav={
            showOperatorSidebar ? (
              <>
                <div className="nav-sidebar-divider" role="separator" aria-label="Selected operator">
                  <span className="nav-sidebar-divider-label">Selected operator</span>
                </div>
                <div className="nav-section nav-section-content nav-section-operator-panel">
                  <div className="nav-content-operator-label" title="Content for this operator">
                    <span className="nav-content-operator-label-text">
                      {menuOperatorId ? getOperatorDisplayName(menuOperatorId) : 'Operator'}
                    </span>
                  </div>
                  <div className="nav-tree-scroll-region">
                    <div className="nav-tree nav-tree-single-operator">
                      {visibleMenuTree.map((node) => renderNavNode(node))}
                    </div>
                  </div>
                </div>
              </>
            ) : null
          }
        />
        <main
          ref={mainRef}
          className={[
            'main',
            isSitemapContentView ? '' : 'main-narrow-surface',
            isHomeMapExplorer ? 'main--home-map' : '',
            isMapPrimaryFluidPage ? 'main--operator-map' : '',
            isAssetsMapHub ? 'main--all-assets' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div
            className={[
              'main-content',
              isSitemapContentView ? 'main-content-operator' : 'main-content-narrow',
              isHomeMapExplorer ? 'main-content--home-map' : '',
              isMapPrimaryFluidPage ? 'main-content--operator-map' : '',
              isAssetsMapHub ? 'main-content--all-assets' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
          {showOperatorDownloadSurface ? (
            <DownloadOperatorsScreen
              homeOnboarding={screenId === HOME_SCREEN && showOperatorDownloadSurface}
              onContinue={(operatorId) => {
                if (operatorId) {
                  goToOperatorScreen(operatorId, DEFAULT_SCREEN)
                } else {
                  navigateToOperatorsIndex()
                }
              }}
            />
          ) : screenId === TRAINING_SCREEN ? (
            <TrainingPortalScreen primaryHex={appBarPrimaryHex} />
          ) : screenId === TRAINING_SCENARIOS_SCREEN ? (
            <TrainingDocumentsListScreen
              featureTarget={TRAINING_FEATURE_SCENARIO}
              listScreenId={TRAINING_SCENARIOS_SCREEN}
            />
          ) : screenId === TRAINING_TOOLBOX_TIPS_SCREEN ? (
            <TrainingDocumentsListScreen
              featureTarget={TRAINING_FEATURE_TOOLBOX_TIP}
              listScreenId={TRAINING_TOOLBOX_TIPS_SCREEN}
            />
          ) : screenId === TRAINING_SCENARIO_DETAIL_SCREEN ? (
            <TrainingDocumentDetailScreen
              screenId={TRAINING_SCENARIO_DETAIL_SCREEN}
              trainingDocId={trainingDocId}
              primaryHex={appBarPrimaryHex}
            />
          ) : screenId === TRAINING_TOOLBOX_TIP_DETAIL_SCREEN ? (
            <TrainingDocumentDetailScreen
              screenId={TRAINING_TOOLBOX_TIP_DETAIL_SCREEN}
              trainingDocId={trainingDocId}
              primaryHex={appBarPrimaryHex}
            />
          ) : screenId === TRAINING_LMS_SCREEN ? (
            <section className="app-download-card">
              <h2 className="app-download-title">Begin Training Modules</h2>
              <p className="app-training-intro">
                Access your Buxus Training seamlessly! Simply click the button below to launch your training
                module. No need to log in – your Buxus credentials will automatically grant you access.
              </p>
              <div className="app-training-actions">
                <a
                  href={LMS_START_URI}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="app-training-cta app-training-cta-with-icon app-download-btn primary"
                  style={{ backgroundColor: appBarPrimaryHex, borderColor: appBarPrimaryHex }}
                >
                  <span>Open Training Modules</span>
                  <NavMdiIcon path={mdiOpenInNew} size={1.2} className="app-training-cta-icon" />
                </a>
              </div>
            </section>
          ) : screenId === ALERT_SCREEN ? (
            !abp.sessionContextLoaded ? (
              <section className="app-download-card">
                <p className="app-download-empty">Loading…</p>
              </section>
            ) : !abp.canSendAlerts ? (
              <section className="app-download-card">
                <h2 className="app-download-title">Send Alert</h2>
                <p className="app-download-empty">You do not have permission to send alerts.</p>
              </section>
            ) : (
            <SendAlertScreen
              model={sendAlertScreenModel}
              senderEmail={abp.bootstrap?.email ?? null}
              primaryHex={appBarPrimaryHex}
            />
            )
          ) : screenId === PROFILE_SCREEN ? (
            <section className="app-download-card">
              <h2 className="app-download-title">Profile</h2>
              <ProfileScreen />
            </section>
          ) : screenId === INFORMATION_SCREEN ? (
            <section className="app-download-card">
              <h2 className="app-download-title">Help</h2>
              {requestType === 'Request an Unlisted Operator' ? (
                <RequestFormPanel
                  operatorId={selectedOperatorId ?? downloadedOperators[0]?.operatorId ?? ''}
                  requestType={requestType}
                  doneLabel="Back to Help"
                  analytics={interactionContext}
                  onCancel={() => navigate('/information')}
                />
              ) : (
                <InformationScreen
                  stakeholderCode={effectiveStakeholderCode}
                  profileStakeholderRaw={profileStakeholderRaw}
                  availableStakeholderCodes={availableStakeholderCodes}
                  analytics={interactionContext}
                  onRequestUnlistedOperator={() =>
                    navigate('/information?rt=Request%20an%20Unlisted%20Operator')
                  }
                />
              )}
            </section>
          ) : screenId === HOME_SCREEN ? (
            <section className="app-download-card app-download-card--home-map">
              {downloadedOperators.length === 0 ? (
                <p className="app-download-empty">No downloaded operators yet. Go to Download first.</p>
              ) : (
                <HomeOperatorsMapScreen
                  operators={downloadedOperators}
                  mapMode={homeMapMode}
                  stakeholderCode={homeScopedStakeholderCode}
                  getOperatorPayload={getCachedOperatorPayload}
                  ensureOperatorPayload={ensureOperatorPayload}
                  jurisdictionCountyStateKeys={jurisdictionKeysForMap}
                  pipelineOperatorDirectory={mapPipelineOperatorDirectory}
                  onViewPipelineOperator={onViewPipelineOperator}
                  analytics={interactionContext}
                />
              )}
            </section>
          ) : screenId === '__operators__' ? (
            <section className="app-download-card">
              <h2 className="app-download-title">Operators</h2>
              {downloadedOperators.length === 0 ? (
                <p className="app-download-empty">No downloaded operators yet. Go to Download first.</p>
              ) : (
                <ul className="app-download-list operators-operator-list">
                  {downloadedOperators.map((op) => {
                    return (
                    <li key={op.operatorId}>
                      <Link
                        to={pathForAppState(op.operatorId, [DEFAULT_SCREEN], urlSlugForOperator)}
                        className="operator-card-link operator-nav-card"
                      >
                      <div className="app-download-row-main operator-card-row-head">
                        <div className="operator-card-title-block">
                          <span className="app-download-name">{op.name}</span>
                          <span className="app-download-meta">v{op.cachedVersion}</span>
                        </div>
                      </div>
                      <OperatorLegendBar
                        items={operatorLegendItems(op.operatorId)}
                        ariaLabel={`${op.name} map legend`}
                      />
                      </Link>
                    </li>
                  )})}
                </ul>
              )}
            </section>
          ) : resolved ? (
            <div
              className={[
                isMapPrimaryFluidPage ? 'operator-map-page' : 'sitemap-content-page',
                isAssetsMapHub ? 'all-assets-host' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {breadcrumbItems.length > 0 ? (
                <div className="page-breadcrumbs page-breadcrumbs-top">
                  {breadcrumbItems.map((item, idx) => (
                    <span key={`${item.label}-${idx}`}>
                      {item.active ? (
                        <span className="page-breadcrumb-current">{item.label}</span>
                      ) : (
                        <Link
                          to={item.to}
                          className="page-breadcrumb-link"
                          onClick={() =>
                            trackMenuItemSelected({
                              ...interactionContext,
                              component: 'Breadcrumb',
                              menu: 'page_breadcrumb',
                              selection: item.label,
                              navigationSource: 'breadcrumb',
                            })
                          }
                        >
                          {item.label}
                        </Link>
                      )}
                      {idx < breadcrumbItems.length - 1 ? <span className="page-breadcrumb-sep"> &gt; </span> : null}
                    </span>
                  ))}
                </div>
              ) : null}
              <div className="page-header-block">
                <div className="page-header-title-row">
                  <div className="page-header-title-group">
                    <p className="page-header-title">{operatorName}</p>
                    {showAssetInHeader && headerAssetDisplayName ? (
                      <p className="page-header-subtitle">{headerAssetDisplayName}</p>
                    ) : allAssetsHeaderLabel ? (
                      <p className="page-header-subtitle">{allAssetsHeaderLabel}</p>
                    ) : null}
                  </div>
                  {selectedOperatorId ? (
                    <OperatorPageHeaderLogo
                      operatorId={selectedOperatorId}
                      logoUrl={logoSrc}
                      logoBlob={logoBlobRef}
                      operatorName={operatorName}
                    />
                  ) : null}
                </div>
              </div>
              <div className="page-stack">
              {isOperatorOverviewMap && selectedOperatorId && (!payload || !assetMapFiltersReady) ? (
                <div className="main-session-loading main-session-loading--inset">
                  <AppLoadingRow />
                </div>
              ) : null}
              {isOperatorOverviewMap && payload && assetMapFiltersReady ? (
                <OperatorOverviewMapScreen
                  payload={payload}
                  resolveContext={contentResolveContext}
                  stakeholderCode={effectiveStakeholderCode}
                  jurisdictionCountyStateKeys={jurisdictionKeysForMap}
                  pipelineOperatorDirectory={mapPipelineOperatorDirectory}
                  onViewPipelineOperator={onViewPipelineOperator}
                  onFocusAssetInSidebar={onFocusAssetFromMap}
                  contextAssetId={sidebarAssetIdForMenu}
                  analytics={interactionContext}
                />
              ) : null}
              {isAssetsMapHub && selectedOperatorId && (!payload || !assetMapFiltersReady) ? (
                <div className="main-session-loading main-session-loading--inset">
                  <AppLoadingRow />
                </div>
              ) : null}
              {isAssetsMapHub && payload && assetMapFiltersReady ? (
                <AllAssetsHubScreen
                  payload={payload}
                  resolveContext={contentResolveContext}
                  stakeholderCode={effectiveStakeholderCode}
                  jurisdictionCountyStateKeys={jurisdictionKeysForMap}
                  pipelineOperatorDirectory={mapPipelineOperatorDirectory}
                  onViewPipelineOperator={onViewPipelineOperator}
                  onSelectAsset={onSelectAssetFromMap}
                  onFocusAssetInSidebar={onFocusAssetFromMap}
                  contextAssetId={sidebarAssetIdForMenu}
                  analytics={interactionContext}
                />
              ) : null}
              {isAssetOverviewMap ? (
                <MapSurface variant="fluid">
                  <MapView
                    key={`map-asset-overview-${activeAssetId ?? 'none'}-${location.key}`}
                    payload={payload ?? undefined}
                    stakeholderCode={effectiveStakeholderCode}
                    activeAssetId={activeAssetId}
                    embedded
                    mapViewOverride={assetMapViewOverride}
                    jurisdictionCountyStateKeys={jurisdictionKeysForMap}
                    pipelineOperatorDirectory={mapPipelineOperatorDirectory}
                    onViewPipelineOperator={onViewPipelineOperator}
                    analytics={interactionContext}
                  />
                </MapSurface>
              ) : null}
              {screenId === HAZARD_SCREEN_ID && hazardStorageMapView ? (
                <MapView
                  key={`map-hazard-storage-${activeAssetId ?? 'na'}-${hazardId ?? hazardProductSubtype ?? 'na'}`}
                  mapRole="hazardStorage"
                  hazardMapView={hazardStorageMapView}
                  embedded
                  jurisdictionCountyStateKeys={jurisdictionKeysForMap}
                  analytics={interactionContext}
                />
              ) : null}
              {payloadError ? <p className="app-download-error">{payloadError}</p> : null}
              {resolved.blocks.map((block, i) => {
                if (block.kind === 'richHtml') {
                  return (
                    <div
                      key={i}
                      className="rich"
                      dangerouslySetInnerHTML={{ __html: block.html }}
                      onClick={(e) => trackRichHtmlLinkClick(e, interactionContext)}
                    />
                  )
                }
                if (block.kind === 'links') {
                  return (
                    <section key={i} className="app-link-section">
                      <div className="child-nav" role="navigation">
                        {block.items.map((item, j) =>
                          item.url?.trim() ? (
                            <a
                              key={j}
                              className="child-nav-btn"
                              href={item.url}
                              target="_blank"
                              rel="noreferrer"
                              onClick={() =>
                                trackUrlPressed({
                                  ...interactionContext,
                                  url: item.url!.trim(),
                                  title: item.label,
                                })
                              }
                            >
                              <span>{item.label}</span>
                              <span className="child-nav-chevron" aria-hidden>
                                →
                              </span>
                            </a>
                          ) : (
                            <div key={j} className="child-nav-btn app-link-btn-static">
                              <strong>{item.label}</strong>
                              <span className="app-link-btn-note">(no URL in operator data)</span>
                            </div>
                          ),
                        )}
                      </div>
                    </section>
                  )
                }
                if (block.kind === 'gallery') {
                  return selectedOperatorId ? (
                    <OperatorImageGallery key={i} operatorId={selectedOperatorId} items={block.items} analytics={interactionContext} />
                  ) : (
                    <p key={i} className="operator-inline-image-fallback">
                      Select an operator to view images.
                    </p>
                  )
                }
                if (block.kind === 'contactCards') {
                  return (
                    <div key={i} className="contact-card-list">
                      {block.items.map((c) => (
                        <ContactCardView
                          key={c.id ?? c.displayName}
                          contact={c}
                          operatorId={selectedOperatorId}
                          analytics={interactionContext}
                          profileUrl={
                            selectedOperatorId && c.id && !contactId
                              ? appendScreenQueryParams(
                                  pathForAppState(
                                    selectedOperatorId,
                                    childNavStackAppend(stack, 'screen.contacts'),
                                    urlSlugForOperator,
                                    { assetId: contentAssetId },
                                  ),
                                  {
                                    contactId: c.id,
                                    contactArea: contactArea ?? c.contactArea,
                                  },
                                )
                              : null
                          }
                        />
                      ))}
                    </div>
                  )
                }
                if (block.kind === 'corporateContactCard') {
                  return selectedOperatorId ? (
                    <div key={i} className="corporate-contact-card">
                      <p className="corporate-contact-card-label">{block.label}</p>
                      <Link
                        className="corporate-contact-card-name"
                        to={appendScreenQueryParams(
                          pathForAppState(
                            selectedOperatorId,
                            childNavStackAppend(stack, 'screen.contacts'),
                            urlSlugForOperator,
                            { assetId: contentAssetId },
                          ),
                          { contactId: block.contactId },
                        )}
                      >
                        {block.displayName}
                      </Link>
                    </div>
                  ) : (
                    <div key={i} className="corporate-contact-card">
                      <p className="corporate-contact-card-label">{block.label}</p>
                      <p className="corporate-contact-card-name">{block.displayName}</p>
                    </div>
                  )
                }
                if (block.kind === 'makeARequestHub') {
                  return (
                    <MakeARequestPanel
                      key={i}
                      emergencyNumber={block.emergencyNumber}
                      buttons={block.buttons}
                      analytics={interactionContext}
                      onSelectRequest={(rt) => {
                        if (!selectedOperatorId) return
                        navigate(
                          appendScreenQueryParams(makeARequestHubPath(selectedOperatorId, urlSlugForOperator), {
                            requestType: rt,
                          }),
                        )
                      }}
                    />
                  )
                }
                if (block.kind === 'requestForm') {
                  if (!selectedOperatorId || !payload) return null
                  const operator = payload.operator as Record<string, unknown>
                  const explanation =
                    typeof operator.explanation === 'string' ? operator.explanation : undefined
                  return (
                    <RequestFormPanel
                      key={i}
                      operatorId={selectedOperatorId}
                      requestType={block.requestType}
                      siteExplanation={explanation}
                      analytics={interactionContext}
                      onCancel={() => {
                        if (!selectedOperatorId) return
                        navigate(makeARequestHubPath(selectedOperatorId, urlSlugForOperator))
                      }}
                    />
                  )
                }
                if (block.kind === 'ctaLink') {
                  return (
                    <div key={i} className="app-cta-card">
                      <a
                        className="app-cta-btn"
                        href={block.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {block.label}
                      </a>
                    </div>
                  )
                }
                if (block.kind === 'hubExternalLink') {
                  return (
                    <div key={i} className="app-hub-external">
                      <a
                        className="app-hub-external-link"
                        href={block.url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() =>
                          trackUrlPressed({
                            ...interactionContext,
                            url: block.url,
                            title: block.label,
                          })
                        }
                      >
                        {block.label}
                      </a>
                    </div>
                  )
                }
                if (block.kind === 'noticeCard') {
                  if (
                    block.message === 'Hazard not found for this asset.' &&
                    screenId === HAZARD_SCREEN_ID &&
                    hazardId?.trim() &&
                    payload &&
                    !contentAssetId
                  ) {
                    return (
                      <p key={i} className="app-download-empty">
                        Loading hazard…
                      </p>
                    )
                  }
                  return (
                    <div
                      key={i}
                      className={block.tone === 'warning' ? 'app-notice-card warning' : 'app-notice-card'}
                    >
                      {block.message}
                    </div>
                  )
                }
                if (block.kind === 'resourceSection') {
                  return (
                    <section key={i} className="resource-section">
                      <h3 className="content-section-heading">{block.title}</h3>
                      <div className="child-nav" role="navigation">
                        {block.items.map((item, j) => (
                          <a
                            key={j}
                            className="child-nav-btn"
                            href={item.url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() =>
                              trackUrlPressed({
                                ...interactionContext,
                                url: item.url,
                                title: item.label,
                              })
                            }
                          >
                            <span>{item.label}</span>
                            <span className="child-nav-chevron" aria-hidden>
                              →
                            </span>
                          </a>
                        ))}
                      </div>
                    </section>
                  )
                }
                if (block.kind === 'emergencyCallButton') {
                  return <EmergencyDialAction key={i} phone={block.phone} analytics={interactionContext} />
                }
                if (block.kind === 'companyErpPicker') {
                  return (
                    <CompanyErpPicker
                      key={i}
                      items={block.items}
                      openingFileId={openingOperatorFileId}
                      onPlanSelected={(item) =>
                        trackMenuItemSelected({
                          screen: interactionContext.screen,
                          operator: interactionContext.operator,
                          asset: interactionContext.asset,
                          component: 'CompanyErpPicker',
                          menu: 'Emergency Response Plan',
                          selection: item.label,
                        })
                      }
                      onOpenFile={(item) =>
                        handleOpenOperatorFile(item.id, {
                          fileExt: item.fileExt,
                          fileName: item.fileName,
                          displayName: item.label,
                        })
                      }
                    />
                  )
                }
                if (block.kind === 'companyResponseAccordions') {
                  return <CompanyResponseAccordions key={i} sections={block.sections} analytics={interactionContext} />
                }
                if (block.kind === 'responderActionsAccordions') {
                  return <ResponderActionsAccordions key={i} sections={block.sections} analytics={interactionContext} />
                }
                if (block.kind === 'radioFrequencyAccordions') {
                  return <RadioFrequencyAccordions key={i} sections={block.sections} analytics={interactionContext} />
                }
                if (block.kind === 'keyLocationAccordions') {
                  if (!selectedOperatorId || !contentAssetId) return null
                  return (
                    <KeyLocationAccordions
                      key={i}
                      sections={block.sections}
                      operatorId={selectedOperatorId}
                      activeAssetId={contentAssetId}
                      urlSlugForOperator={urlSlugForOperator}
                      analytics={interactionContext}
                    />
                  )
                }
                if (block.kind === 'documentSection') {
                  return (
                    <section key={i} className="asset-documents-section resource-documents-section">
                      <h3 className="content-section-heading">{block.title}</h3>
                      <ul className="asset-documents-file-list">
                        {block.items.map((doc) => {
                          const isOpening = openingOperatorFileId === doc.id
                          return (
                            <li key={doc.id}>
                              <button
                                type="button"
                                className="asset-documents-file-row"
                                disabled={!!openingOperatorFileId}
                                aria-busy={isOpening}
                                onClick={() =>
                                  void handleOpenOperatorFile(doc.id, {
                                    fileExt: doc.fileExt,
                                    fileName: doc.fileName,
                                    displayName: doc.label,
                                  })
                                }
                              >
                                <span className="asset-documents-file-icon" aria-hidden>
                                  📄
                                </span>
                                <span className="asset-documents-file-text">
                                  <span className="asset-documents-file-title">
                                    {doc.label}
                                    {isOpening ? <InlineSpinner label="Opening file" /> : null}
                                  </span>
                                </span>
                              </button>
                            </li>
                          )
                        })}
                      </ul>
                    </section>
                  )
                }
                if (block.kind === 'facilityProfileCard') {
                  if (!selectedOperatorId || !contentAssetId) return null
                  return (
                    <FacilityProfileCard
                      key={i}
                      model={block.model}
                      operatorId={selectedOperatorId}
                      activeAssetId={contentAssetId}
                      urlSlugForOperator={urlSlugForOperator}
                      analytics={interactionContext}
                    />
                  )
                }
                if (block.kind === 'productsHazardsBrowse') {
                  if (!selectedOperatorId || !contentAssetId) return null
                  return (
                    <ProductsHazardsBrowse
                      key={i}
                      model={block.model}
                      operatorId={selectedOperatorId}
                      stack={stack}
                      urlSlugForOperator={urlSlugForOperator}
                      activeAssetId={contentAssetId}
                      onNavigate={commitSidebarAssetSelection}
                      analytics={interactionContext}
                    />
                  )
                }
                if (block.kind === 'hazardDetail') {
                  const sdsId = block.model.sdsFileId
                  return (
                    <HazardDetailView
                      key={i}
                      model={block.model}
                      operatorId={selectedOperatorId}
                      analytics={interactionContext}
                      sdsOpening={!!sdsId && openingOperatorFileId === sdsId}
                      onViewSds={
                        sdsId
                          ? () =>
                              void handleOpenOperatorFile(sdsId, {
                                fileExt: block.model.sdsFileExt,
                                fileName: block.model.sdsFileName,
                                displayName: block.model.sdsTitle ?? undefined,
                              })
                          : undefined
                      }
                    />
                  )
                }
                if (block.kind === 'assetDocumentsList') {
                  return (
                    <AssetDocumentsList
                      key={i}
                      model={block.model}
                      openingFileId={openingOperatorFileId}
                      onOpenFile={(row) =>
                        handleOpenOperatorFile(row.id, {
                          fileExt: row.fileExt,
                          fileName: row.fileName,
                          displayName: row.title,
                        })
                      }
                    />
                  )
                }
                if (block.kind === 'branchHub') {
                  if (block.introHtml?.trim()) {
                    return (
                      <div
                        key={i}
                        className="rich"
                        dangerouslySetInnerHTML={{ __html: block.introHtml }}
                        onClick={(e) => trackRichHtmlLinkClick(e, interactionContext)}
                      />
                    )
                  }
                  if (skipContentChildNav || block.items.length === 0) return null
                  return (
                    <div key={i} className="child-nav" role="navigation">
                      {block.items.map((item, j) => (
                        <Link
                          key={`${item.targetScreenId}-${item.assetId ?? 'na'}-${j}`}
                          to={
                            selectedOperatorId
                              ? (() => {
                                  const nextStack = childNavStackAppend(stack, item.targetScreenId)
                                  const itemAssetId = item.assetId ?? contentAssetId
                                  const base = pathForAppState(
                                    selectedOperatorId,
                                    nextStack,
                                    urlSlugForOperator,
                                    { assetId: itemAssetId },
                                  )
                                  return appendScreenQueryParams(base, item)
                                })()
                              : '/operators'
                          }
                          className="child-nav-btn"
                          onClick={() => {
                            trackMenuItemSelected({
                              ...interactionContext,
                              component: 'ContentChildNav',
                              menu: screenId === 'screen.contacts' ? 'Contacts' : interactionContext.screen,
                              selection: item.label,
                              navigationSource: 'content_child_nav',
                              ...(item.contactArea ? { contactArea: item.contactArea } : {}),
                            })
                            const nextStack = childNavStackAppend(stack, item.targetScreenId)
                            const itemAssetId = item.assetId ?? contentAssetId ?? null
                            sidebarHighlightOverrideRef.current = highlightContextFromStack(
                              nextStack,
                              itemAssetId,
                            )
                            if (item.assetId) commitSidebarAssetSelection(item.assetId)
                            else clearContentAssetSelection()
                          }}
                        >
                          <span>{item.label}</span>
                          <span className="child-nav-chevron" aria-hidden>
                            →
                          </span>
                        </Link>
                      ))}
                    </div>
                  )
                }
                if (block.kind === 'childNav') {
                  if (skipContentChildNav) return null
                  return (
                    <div key={i} className="child-nav" role="navigation">
                      {block.items.map((item, j) => (
                        <Link
                          key={`${item.targetScreenId}-${item.assetId ?? 'na'}-${j}`}
                          to={
                            selectedOperatorId
                              ? (() => {
                                  const nextStack = childNavStackAppend(stack, item.targetScreenId)
                                  const itemAssetId = item.assetId ?? contentAssetId
                                  const base = pathForAppState(
                                    selectedOperatorId,
                                    nextStack,
                                    urlSlugForOperator,
                                    { assetId: itemAssetId },
                                  )
                                  return appendScreenQueryParams(base, item)
                                })()
                              : '/operators'
                          }
                          className="child-nav-btn"
                          onClick={() => {
                            trackMenuItemSelected({
                              ...interactionContext,
                              component: 'ContentChildNav',
                              menu: screenId === 'screen.contacts' ? 'Contacts' : interactionContext.screen,
                              selection: item.label,
                              navigationSource: 'content_child_nav',
                              ...(item.contactArea ? { contactArea: item.contactArea } : {}),
                            })
                            const nextStack = childNavStackAppend(stack, item.targetScreenId)
                            const itemAssetId = item.assetId ?? contentAssetId ?? null
                            sidebarHighlightOverrideRef.current = highlightContextFromStack(
                              nextStack,
                              itemAssetId,
                            )
                            if (item.assetId) commitSidebarAssetSelection(item.assetId)
                            else clearContentAssetSelection()
                          }}
                        >
                          <span>{item.label}</span>
                          <span className="child-nav-chevron" aria-hidden>
                            →
                          </span>
                        </Link>
                      ))}
                    </div>
                  )
                }
                return null
              })}
              </div>
            </div>
          ) : screenId !== '__download__' &&
            screenId !== '__operators__' &&
            screenId !== HOME_SCREEN &&
            !isTrainingShellScreen(screenId) &&
            screenId !== ALERT_SCREEN &&
            screenId !== PROFILE_SCREEN &&
            screenId !== INFORMATION_SCREEN ? (
            selectedOperatorId && !payload && !payloadError ? (
              <p className="app-download-empty">Loading operator…</p>
            ) : selectedOperatorId && payloadError ? (
              <p className="app-download-error">{payloadError}</p>
            ) : (
              <p className="app-download-empty">Unknown screen.</p>
            )
          ) : null}
          </div>
        </main>
      </div>
    </div>
  )
}
